// ============================================================
// ApiClient — Bearer-token-attaching fetch wrapper.
//
// Resolves the access token from the current Supabase session
// (via @square/core/createBrowserClient) and attaches it to every
// outbound /api/* call. Returns parsed JSON or throws ApiError.
//
// Server-side fetch is intentionally not handled here — this is
// a browser-only helper. The shell consumes it through hooks
// inside React components.
// ============================================================

import { tryCreateBrowserClient } from '@square/core'
import type { SupabaseClient } from '@supabase/supabase-js'

export class ApiError extends Error {
  status: number
  code: string
  constructor(status: number, code: string, message: string) {
    super(message)
    this.status = status
    this.code = code
    this.name = 'ApiError'
  }
}

let cachedClient: SupabaseClient | null = null
let tried = false
function browser(): SupabaseClient | null {
  if (!tried) {
    cachedClient = tryCreateBrowserClient()
    tried = true
  }
  return cachedClient
}

async function getAccessToken(): Promise<string | null> {
  const supabase = browser()
  if (!supabase) return null
  try {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  } catch {
    return null
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | null | undefined>
  signal?: AbortSignal
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === '') continue
    params.set(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const url = buildUrl(path, opts.query)
  const method = opts.method || (opts.body ? 'POST' : 'GET')

  const headers: Record<string, string> = {
    'Accept': 'application/json',
  }

  const token = await getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  let body: BodyInit | undefined
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(opts.body)
  }

  const res = await fetch(url, { method, headers, body, signal: opts.signal })

  let payload: any = null
  const ct = res.headers.get('content-type') || ''
  if (ct.includes('application/json')) {
    try { payload = await res.json() } catch { payload = null }
  }

  if (!res.ok) {
    // Normalize: payload.error and payload.message may be objects
    // (e.g. an error object serialized by an upstream layer). The
    // shell used to render `[object Object]: request failed` when
    // either field came back as a non-string. Coerce to string here
    // so callers always get a readable ApiError.
    const codeRaw = payload?.error
    const code =
      typeof codeRaw === 'string'
        ? codeRaw
        : codeRaw && typeof codeRaw === 'object'
          ? (codeRaw.code || codeRaw.name || `http_${res.status}`)
          : `http_${res.status}`

    const messageRaw = payload?.message
    let message: string
    if (typeof messageRaw === 'string' && messageRaw) {
      message = messageRaw
    } else if (messageRaw && typeof messageRaw === 'object') {
      message =
        (messageRaw as any).message ||
        (messageRaw as any).error ||
        JSON.stringify(messageRaw)
    } else {
      // No structured message — prefer the code as the user-facing
      // string (e.g. "missing_token", "forbidden", "rate_limited")
      // instead of a generic "request failed". The HTTP status is
      // still on err.status for callers that want it.
      message =
        (typeof code === 'string' && code && !code.startsWith('http_'))
          ? humanizeCode(code)
          : res.statusText || `request failed (${res.status})`
    }

    throw new ApiError(res.status, String(code), String(message))
  }

  return payload as T
}

// ----------------------------------------------------------------
// Small auth-error helpers. Components use these to render a clean
// "auth required" state instead of a generic error banner when the
// failure is just "no bearer token attached" — which is the
// expected pre-sign-in state, not a system fault.
// ----------------------------------------------------------------

export function isAuthError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false
  if (err.status === 401) return true
  return (
    err.code === 'missing_token' ||
    err.code === 'invalid_token' ||
    err.code === 'token_verify_failed'
  )
}

export function isForbiddenError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 403 || err.code === 'forbidden')
}

function humanizeCode(code: string): string {
  // Light cosmetic touch — turn snake_case into a sentence so the
  // raw code is readable in an EmptyState body. Stays close to the
  // wire form so operators can still grep logs.
  return code.replace(/_/g, ' ')
}

// Convenience namespaced helpers — type-safe surface that mirrors
// the api/ directory layout. Each module is small enough that we
// inline the fetches here rather than splitting into more files.

export interface AllowlistResult {
  ok: true
  approved: boolean
  source?: string
  identity?: string
  reason?: string
}

// ── End User Portal: owner grant views + recipient manifest ──────
// Mirrors api/access/grants.ts grantView() and api/_lib/portal-manifest.ts.
export interface AccessGrantView {
  id: string
  project_id: string
  portal_id: string | null
  invite_id: string
  invite_path: string
  label: string
  recipient: { name?: string; email?: string }
  nda_required: boolean
  status: 'issued' | 'redeemed' | 'suspended' | 'revoked' | 'expired'
  issued_at: string
  redeemed_at: string | null
  suspended_at: string | null
  revoked_at: string | null
  engine_confirmed_revoked: boolean
}

export interface RecipientManifest {
  workspace: { name: string; summary: string | null }
  modules: Array<{ key: string; label: string; state: 'live' | 'coming_soon' }>
  permissions: { role: 'guest'; can_write: false }
  onboarding: {
    nda: { required: boolean; version: string; accepted: boolean; accepted_at: string | null }
    preferences: { set: boolean; portal_notifications: boolean; email_updates: boolean }
    complete: boolean
  }
  session: { expires_at: string | null }
  branding: { product_name: string; client_brand: string | null }
  preview: boolean
}

export interface PortalSessionManifestResponse {
  ok: boolean
  state: string
  manifest?: RecipientManifest
  message?: string
}

// ── Capability manifest (GET /api/capabilities, packet §05) ──────
// Mirrors api/_lib/capabilities.ts exactly — the implemented shape,
// not the packet contract doc (which predates `degraded`/`reason`).
export type CapabilityState =
  | 'live'
  | 'manual'
  | 'preview'
  | 'degraded'
  | 'blocked'
  | 'unavailable'

export interface Capability {
  key: string
  state: CapabilityState
  reason: string
  source: 'env' | 'schema' | 'authz' | 'server'
  requiresApproval?: boolean
  /** Owner/admin-only remediation hint; null for non-privileged callers or when live. */
  action: string | null
  last_verified_at: string
}

export interface CapabilityManifest {
  generated_at: string
  environment: string
  capabilities: Record<string, Capability>
}

export interface BootstrapResult {
  ok: true
  user: { id: string; email: string | null }
  session_id: string | null
  org_id: string | null
  role: { id: string; name: string; level: number; permissions: string[] } | null
  created_org: boolean
}

export interface ProjectApiRow {
  id: string
  owner_user_id: string
  org_id: string | null
  name: string
  slug: string
  client_name: string | null
  status: string
  domain: string | null
  health_score: number | null
  metadata_json: Record<string, unknown>
  created_at: string
  updated_at: string
}

// ---- Square Command Center (operator cockpit) row shapes ----
// Access codes are always redacted on the wire: code_prefix (display
// only) is present, but code_hash and the one-time plaintext never are.
export interface CommandCenterAccessCode {
  id: string
  project_id: string | null
  label: string
  audience: string
  role: string
  code_prefix: string
  status: string
  max_uses: number
  use_count: number
  created_by: string
  claimed_by: string | null
  claimed_at: string | null
  revoked_at: string | null
  revoke_reason: string | null
  expires_at: string | null
  created_at: string
  updated_at: string
  // Secure Access target binding — where this code opens on redeem.
  // Null on unbound legacy/operator codes.
  target_type: string | null
  target_id: string | null
  target_label: string | null
  target_destination: string | null
}

export type AccessCodeTargetType =
  | 'client_portal' | 'intake_invite' | 'document_workspace' | 'document_artifact'
  | 'spreadsheet_artifact' | 'project_workspace' | 'secure_portal'
  // PHASE_2L universal Olivium Secure Access targets (kept in lockstep with the
  // DB target_type CHECK + ACCESS_CODE_TARGET_TYPES + the UI target options).
  | 'project_portal' | 'repo_context' | 'command_packet_placeholder'
  | 'document_or_file_placeholder' | 'client_workspace_placeholder'

// ---- Build Now Execute Layer (post jobs) row shapes ----
export interface PostJob {
  id: string
  project_id: string | null
  workspace_label: string | null
  requester_id: string
  title: string
  prompt: string
  job_type: string
  target_surface: string
  deadline_mode: string
  requested_due_at: string | null
  complexity_band: number
  primary_agent_id: string | null
  supporting_agent_ids: string[]
  required_skill_ids: string[]
  approval_gate: string
  state: string
  microjob_count: number
  proof_required: string | null
  receipt_required: boolean
  context_json: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface PostJobRun {
  id: string
  job_id: string
  run_status: string
  active_agent_id: string | null
  active_skill_id: string | null
  complexity_band: number | null
  started_at: string | null
  ended_at: string | null
  created_at: string
}

export interface PostJobMicrojob {
  id: string
  run_id: string
  sequence: number
  title: string
  microjob_type: string | null
  owner_agent_id: string | null
  skill_id: string | null
  status: string
  depends_on: string | null
  proof_required: string | null
  output_summary: string | null
  created_at: string
}

export interface PostJobStatusEvent {
  id: string
  job_id: string
  run_id: string | null
  microjob_id: string | null
  event_code: string
  message: string
  status: string | null
  progress_percent: number | null
  blocker_class: string | null
  evidence_class: string | null
  created_by: string
  created_at: string
}

export interface PostJobApproval {
  id: string
  job_id: string
  run_id: string | null
  /** The Conference Room meeting this approval was raised inside, if any. */
  meeting_id?: string | null
  approval_gate: string
  required_by: string | null
  decision: 'pending' | 'approved' | 'rejected' | 'expired'
  decision_note: string | null
  decided_by: string | null
  decided_at: string | null
  created_at: string
}

export interface PostJobArtifact {
  id: string
  job_id: string
  run_id: string | null
  microjob_id: string | null
  artifact_type: string
  title: string
  uri: string | null
  content_preview: string | null
  created_by: string | null
  created_at: string
}

export interface PostJobReceipt {
  id: string
  job_id: string
  run_id: string | null
  receipt_type: string
  evidence_class: string
  summary: string
  next_action: string | null
  created_by: string
  created_at: string
}

// Execution-engine records (Design Creation Execution Engine).
export interface DesignJobDetails {
  id: string
  job_id: string
  user_intent: string | null
  requested_outputs: string[]
  destination_scope: 'draft_only' | 'internal_marketing' | 'external_delivery' | 'publish_or_blast'
  approval_posture: string
  brand_context_json: Record<string, unknown>
  recipe_id: string | null
  parent_job_id: string | null
  engine_depth: number
  created_at: string
}

export interface DesignArtifactVersion {
  id: string
  artifact_id: string
  version_number: number
  change_summary: string | null
  content: string
  content_format: 'markdown' | 'svg' | 'json' | 'text'
  created_by: string
  created_at: string
}

export interface DesignQualityEval {
  id: string
  job_id: string
  artifact_id: string | null
  eval_type: string
  score: number | null
  pass_fail: 'pass' | 'fail' | 'review_needed'
  findings_json: Array<{ check: string; outcome: string; detail: string }>
  created_by: string
  created_at: string
}

export interface JobProviderReceipt {
  id: string
  job_id: string
  run_id: string | null
  provider_id: string
  request_type: string
  status: 'prepared' | 'submitted' | 'received' | 'rejected' | 'blocked'
  output_refs_json: string[]
  usage_rights_note: string | null
  created_at: string
}

export interface JobRecipe {
  id: string
  title: string
  description: string | null
  job_type: string
  target_surface: string
  approval_gate: string
  prompt_template: string
  requested_outputs: string[]
  plan_json: unknown
  chained_recipe_ids: string[]
  source_job_id: string | null
  times_used: number
  last_used_at: string | null
  is_active: boolean
  created_at: string
}

export interface EngineRunResult {
  ok: boolean
  job_id: string
  job_state: string
  steps_executed: number
  halted: 'completed' | 'awaiting_approval' | 'approval_rejected' | 'step_failed' | 'max_steps' | 'not_runnable'
  message: string
  spawned_job_ids: string[]
}

export interface PostJobDetailResponse {
  ok: true
  job: PostJob
  runs: PostJobRun[]
  microjobs: PostJobMicrojob[]
  status_events: PostJobStatusEvent[]
  approvals: PostJobApproval[]
  artifacts: PostJobArtifact[]
  artifact_versions: DesignArtifactVersion[]
  receipts: PostJobReceipt[]
  design_details: DesignJobDetails | null
  quality_evals: DesignQualityEval[]
  provider_receipts: JobProviderReceipt[]
  child_job_ids: string[]
}

// QCAL-01 Quantum Calendar Agent — calendar intelligence layer. Owner
// and client scope are server-derived (see api/_lib/calendar/actor.ts);
// no client can request a scope directly.
export interface CalendarPlan {
  id: string
  project_id: string | null
  requester_id: string
  actor_scope: 'owner_private' | 'owner_business' | 'client_workspace' | 'client_user_personal' | 'shared_team' | 'mixed_scope'
  actor_role: 'owner' | 'client_admin' | 'client_manager' | 'client_member' | 'agent_manager' | 'execution_agent'
  request_text: string
  window_type: 'day' | 'week' | 'month' | 'quarter' | 'year'
  status: 'captured' | 'recommended' | 'blocked' | 'accepted' | 'rejected' | 'handed_off'
  recommendation: Record<string, unknown>
  alternatives: unknown[]
  rejected_options: unknown[]
  permission_verdict: {
    allowed: boolean
    approvalRequired: boolean
    blockedReason?: string
    allowedActions: string[]
    forbiddenActions: string[]
  }
  metrics: Record<string, number>
  created_at: string
  updated_at: string
}

export interface CalendarPlanEvent {
  id: string
  plan_id: string
  event_type: string
  message: string
  event_payload: Record<string, unknown>
  evidence_class: string
  created_by: string
  created_at: string
}

export interface CalendarHandoff {
  id: string
  plan_id: string
  target_layer: string
  target_payload: Record<string, unknown>
  approval_required: boolean
  status: 'prepared' | 'approved' | 'declined'
  created_job_id: string | null
  created_at: string
}

export interface CalendarWindowInfo {
  window_type: 'day' | 'week' | 'month' | 'quarter' | 'year'
  planning_question: string
  typical_outputs: string[]
  risk_focus: string
}

export interface CalendarWorkspaceItem {
  id: string
  anchor_kind: 'google_event' | 'draft' | 'plan' | 'meeting' | 'daily_meeting' | 'task' | 'day'
  anchor_id: string
  anchor_date: string | null
  item_type: 'note' | 'link' | 'file' | 'message'
  title: string | null
  body: string | null
  url: string | null
  file_bucket: string | null
  file_path: string | null
  file_name: string | null
  metadata: Record<string, unknown>
  created_by: string
  created_at: string
  updated_at: string
}

export interface Meeting {
  id: string
  created_by: string
  project_id: string | null
  job_id: string | null
  context_type: string
  context_id: string | null
  title: string
  meeting_mode: string
  risk_tier: 'low' | 'medium' | 'high' | 'critical'
  status: 'created' | 'token_ready' | 'active' | 'ended' | 'cancelled' | 'failed' | 'blocked' | 'receipted'
  daily_room_name: string
  daily_room_url: string
  starts_at: string | null
  expires_at: string
  max_participants: number
  created_at: string
}

export interface MeetingJoinResult {
  ok: true
  meeting_id: string
  room_url: string
  room_name: string
  token: string
  expires_at: string
  participant_role: string
}

export interface DailyStatus {
  ok: true
  configured: boolean
  daily_domain_present: boolean
  schema_ready: boolean
  schema_error: string | null
  meeting_mode: 'ready_for_internal_test' | 'blocked_missing_api_key' | 'blocked_migration_not_applied'
  checked_at: string
}

export interface RegistryAgent {
  agent_id: string
  display_name: string
  source_system: string | null
  lane: string | null
  status: string | null
  default_gate: string | null
  permission_summary: string | null
  updated_at: string
  skills?: Array<{ skill_id: string; priority: 'primary' | 'supporting' }>
  usage_policies?: Array<{ agent_id: string; route_key: string; model_tier: string; is_enabled: boolean }>
}

export interface WaitingSession {
  id: string
  meeting_id: string
  user_id: string | null
  display_name: string | null
  status: 'waiting' | 'admitted' | 'left' | 'expired'
  entered_at: string
  admitted_at: string | null
  agenda_items?: string[]
  guest_message_count?: number
  last_guest_message?: string | null
}

export interface WaitingMessage {
  id: string
  session_id: string
  sender_type: 'guest' | 'host' | 'agent'
  message_type: 'chat' | 'greeting' | 'agenda_prompt' | 'agenda_response' | 'host_update' | 'admittance'
  body: string
  created_at: string
}

export interface UsageRoutePolicy {
  id: string
  agent_id: string
  route_key: string
  model_tier: 'demo' | 'standard' | 'premium' | 'executive'
  max_requests_per_hour: number
  max_concurrent: number
  max_input_tokens_per_day: number
  max_output_tokens_per_day: number
  max_provider_credits_per_day: number
  is_enabled: boolean
}

export interface UsageDecision {
  ok: true
  allowed: boolean
  decision: 'allow' | 'queue' | 'throttle' | 'approval_required' | 'deny'
  reason: string
  policy: {
    agentId: string; routeKey: string; modelTier: string
    maxRequestsPerHour: number; maxConcurrent: number
    maxInputTokensPerDay: number; maxOutputTokensPerDay: number; maxProviderCreditsPerDay: number
    isEnabled: boolean
  } | null
  snapshot: {
    requestsThisHour: number; concurrentActive: number
    inputTokensToday: number; outputTokensToday: number; providerCreditsToday: number
  } | null
  lease_id?: string | null
}

export interface CommandCenterInvite {
  id: string
  scope: 'project' | 'org'
  email: string
  role: string
  status: string
  project_id: string | null
  org_id: string | null
  expires_at: string | null
  invited_by: string | null
  created_at: string
  updated_at: string | null
}

export interface CommandCenterReceipt {
  id: string
  sequence: number | null
  action: string
  actor_id: string | null
  actor_role: string | null
  resource_type: string
  resource_id: string | null
  severity: string
  outcome: string
  chain_hash: string | null
  created_at: string
}

export interface CommandCenterPermissionOverride {
  id: string
  user_id: string
  org_id: string
  permission: string
  resource_type: string | null
  resource_id: string | null
  granted: boolean
  granted_by: string | null
  reason: string | null
  expires_at: string | null
  created_at: string
}

export interface CommandCenterSession {
  id: string
  user_id: string
  device_fingerprint: string
  ip_address: string | null
  user_agent: string | null
  location_hint: string | null
  status: string
  started_at: string | null
  last_seen_at: string | null
  expires_at: string | null
}

export interface WorkspaceApiRow {
  id: string
  name: string
  slug: string
  scope: 'personal' | 'project'
  owner_user_id: string
  project_id: string | null
  default_mode: string
  current_mode: string
  status: string
  prompt: string | null
  metadata_json: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface WorkspaceRunApiRow {
  id: string
  workspace_id: string
  mode: string
  prompt: string
  status: string
  progress: number
  provider: string
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface WorkspaceEventApiRow {
  id: string
  workspace_id: string
  run_id: string | null
  agent_name: string
  event_type: string
  message: string
  payload_json: Record<string, unknown>
  created_at: string
}

export interface SiteApiRow {
  id: string
  project_id: string
  owner_user_id: string
  name: string
  slug: string
  domain: string | null
  status: string
  metadata_json: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface SitePageApiRow {
  id: string
  site_id: string
  title: string
  path: string
  page_order: number
  status: string
  is_homepage: boolean
  created_at: string
  updated_at: string
}

export interface SiteBlockApiRow {
  id: string
  page_id: string
  block_type: string
  block_order: number
  visibility: string
  locked: boolean
  content_json?: Record<string, unknown>
  style_json?: Record<string, unknown>
  metadata_json?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface SiteAssetApiRow {
  id: string
  site_id: string
  file_name: string
  file_type: string
  file_size: number
  mime_type: string | null
  storage_path: string
  storage_url: string | null
  alt_text: string
  dimensions_json: Record<string, unknown> | null
  metadata_json: Record<string, unknown>
  created_at: string
}

export interface SiteRevisionApiRow {
  id: string
  site_id: string
  revision_number: number
  revision_type: string
  snapshot_json: Record<string, unknown>
  label: string | null
  created_by: string | null
  created_at: string
}

export interface SiteDeploymentHistoryApiRow {
  id: string
  site_id: string
  revision_id: string | null
  status: string
  provider: string
  provider_ref: string | null
  deploy_url: string | null
  error_json: Record<string, unknown> | null
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface HostingDeploymentApiRow {
  id: string
  project_id: string
  site_id: string | null
  environment: 'production' | 'preview' | 'local'
  provider: string
  status: string
  deploy_url: string | null
  build_log_url: string | null
  commit_sha: string | null
  started_at: string | null
  completed_at: string | null
  metadata_json: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface HostingDomainApiRow {
  id: string
  project_id: string
  hostname: string
  environment: string
  status: 'pending' | 'verifying' | 'active' | 'error'
  dns_records_json: unknown[]
  verified_at: string | null
  created_at: string
  updated_at: string
}

// ----------------------------------------------------------------
// Vercel live snapshot rows — returned by /api/hosting/vercel/project-status.
// These are NOT Square Code database rows. They are live Vercel API
// responses, proxied server-side to keep the token off the browser.
// ----------------------------------------------------------------

export interface VercelDeploymentRow {
  uid: string
  name?: string
  url: string
  state: string
  readyState?: string
  created: number
  target?: string | null
  meta?: Record<string, unknown>
}

export interface VercelDomainRow {
  name: string
  apexName?: string
  verified: boolean
  createdAt?: number
}

export interface OliviumAlertApiRow {
  id: string
  org_id: string | null
  user_id: string | null
  alert_type: string
  severity: 'info' | 'warning' | 'critical' | 'alert'
  title: string
  detail: Record<string, unknown>
  status: 'open' | 'acknowledged' | 'investigating' | 'resolved' | 'dismissed' | 'escalated'
  acknowledged_by: string | null
  acknowledged_at: string | null
  resolved_by: string | null
  resolved_at: string | null
  source_event_id: string | null
  created_at: string
  updated_at: string
}

export interface CommandApiResult {
  id: string
  status: string
  command_id: string | null
  args: Record<string, unknown>
  message: string | null
  result: Record<string, unknown>
  error: Record<string, unknown> | null
  job_refs: Record<string, unknown>
  voice_output_url?: string | null
  created_at: string
  completed_at: string | null
}

// Compass natural-language interpretation of an utterance. Returned by
// /api/commands/interpret. `mode==='command'` carries a registry
// command_id + args ready to dispatch; `answer` carries a plain reply;
// `unknown` means the model could not classify it. Interpretation is
// separate from execution — this shape never runs anything.
export interface CommandIntent {
  mode: 'command' | 'answer' | 'unknown'
  command_id: string | null
  args: Record<string, unknown>
  message: string | null
  confidence: 'high' | 'low'
}

export interface TricorderClient {
  id: string; name: string; slug: string; client_name: string | null
  status: string; domain: string | null; health_score: number | null
  github_repo_url: string | null; gitlab_url: string | null
  metadata_json: Record<string, unknown>; created_at: string; updated_at: string
  health: TricorderHealth | null; update_count: number; open_threads: number
}

export interface TricorderUpdate {
  id: string; project_id: string; author_user_id: string
  update_type: string; title: string; body: string | null
  metadata_json: Record<string, unknown>; created_at: string
}

export interface TricorderThread {
  id: string; project_id: string; author_user_id: string
  subject: string; status: string; message_count: number
  last_message_at: string | null; created_at: string; updated_at: string
}

export interface TricorderMessage {
  id: string; thread_id: string; author_user_id: string
  body: string; message_type: string; created_at: string
}

export interface TricorderHealth {
  id: string; project_id: string; status: string
  health_score: number | null; health_source: string
  gitlab_repo: string | null; notes: string | null
  last_contact_at: string | null; created_at: string; updated_at: string
}

// Activation Doctor (/api/square/activation/status) — the five
// self-reported activation dimensions. Detail fields carry booleans,
// alias NAMES, hosts, and counts only; never a secret value.
export interface ActivationCheck {
  key: 'deployment' | 'env' | 'database' | 'google_redirect' | 'agents'
  label: string
  status: 'pass' | 'warn' | 'fail'
  note: string
  detail: Record<string, unknown>
}

export interface ActivationReport {
  verdict: 'ready' | 'attention' | 'blocked'
  readiness_percent: number
  summary: { pass: number; warn: number; fail: number; total: number }
  checks: ActivationCheck[]
  next_actions: string[]
  generated_at: string
}

export const api = {
  auth: {
    /** Hardened login via server path — rate-limited, lockout-aware, audit-logged. */
    login: (email: string, password: string, device_fingerprint?: string) =>
      apiRequest<{
        ok: boolean
        user: { id: string; email: string }
        session: { id: string; expires_at: string; access_token: string; refresh_token: string }
      }>('/api/auth/login', {
        method: 'POST',
        body: { email, password, device_fingerprint },
      }),
    logout:  () => apiRequest('/api/auth/logout',  { method: 'POST' }),
    verify:  () => apiRequest('/api/auth/verify',  { method: 'GET' }),
    refresh: (refresh_token: string) =>
      apiRequest('/api/auth/refresh', { method: 'POST', body: { refresh_token } }),
    bootstrap: (device_fingerprint?: string) =>
      apiRequest<BootstrapResult>('/api/auth/bootstrap', {
        method: 'POST',
        body: { device_fingerprint },
      }),
    allowlistCheck: () =>
      apiRequest<AllowlistResult>('/api/auth/allowlist-check', {
        method: 'POST',
      }),
  },

  projects: {
    list: (q?: { status?: string; include_archived?: boolean }) =>
      apiRequest<{ ok: true; projects: ProjectApiRow[] }>('/api/projects/list', { method: 'GET', query: { status: q?.status, include_archived: q?.include_archived ? 1 : undefined } }),
    get: (id: string) =>
      apiRequest<{ ok: true; project: ProjectApiRow; counters: { open_requests: number | null; active_files: number | null; last_run_at: string | null; latest_run_status: string | null } }>(
        '/api/projects/get', { method: 'GET', query: { id } },
      ),
    create: (payload: { name: string; slug?: string; client_name?: string; status?: string; domain?: string; org_id?: string | null; metadata_json?: Record<string, unknown> }) =>
      apiRequest<{ ok: true; project: ProjectApiRow }>('/api/projects/create', { method: 'POST', body: payload }),
    update: (payload: { id: string; name?: string; client_name?: string | null; status?: string; domain?: string | null; health_score?: number | null; metadata_json?: Record<string, unknown> }) =>
      apiRequest<{ ok: true; project: ProjectApiRow }>('/api/projects/update', { method: 'POST', body: payload }),
    archive: (id: string) =>
      apiRequest<{ ok: true; project: ProjectApiRow }>('/api/projects/archive', { method: 'POST', body: { id } }),
    // Client Access — operator read surface (Phase 1B). Read-only.
    access: (id: string) =>
      apiRequest<{
        ok: true
        activated: boolean
        members: {
          id: string; project_id: string; user_id: string; role: string; status: string
          invited_by: string | null; created_at: string; updated_at: string
        }[]
        invites: {
          id: string; project_id: string; email: string; role: string; status: string
          expires_at: string | null; invited_by: string | null; claimed_at: string | null
          created_at: string; updated_at: string
        }[]
      }>('/api/projects/access/list', { method: 'GET', query: { id } }),
  },

  // Commandery access bridge (Phase 1D-b) — DRY-RUN only. The server routes
  // verify operator ownership and return a clearly-flagged mock ({dry_run:true});
  // no real invite/code/token/hash, no Olivium call. Listing reuses
  // projects.access() above (the existing read). See SQUARE_TO_OLIVIUM_ACCESS_ADAPTER.md.
  access: {
    // Dry-run (Phase 1D-b) OR live (Phase 1D-c, live: true). Live returns the
    // one-time plaintext access_code; a hash is never returned. See
    // SQUARE_TO_OLIVIUM_ACCESS_ADAPTER.md + PHASE_1D_C_LIVE_BRIDGE_READINESS.md.
    // Issue for a project directly, or THROUGH a linked portal (portal_id) —
    // the server re-authorizes both and records the invite ↔ project grant.
    create: (payload: {
      project_id?: string; portal_id?: string; label: string; live?: boolean
      recipient?: string | { name?: string; email?: string }; nda_required?: boolean
      access_type?: string; role?: string; visibility?: string; expiration?: string
      mode?: string; notes?: string
    }) =>
      apiRequest<{
        ok: true; message: string; dry_run?: boolean; live?: boolean
        invite: { id: string; status: string }
        access_code?: string; code_reveal?: 'one_time'; code_storage?: 'kdf_hash_only'
        invite_path?: string; project_id?: string; portal_id?: string | null
        correlation_id?: string; grant_recorded?: boolean
      }>('/api/access/create', { method: 'POST', body: payload }),
    // LIVE two-layer revocation (End User Portal Activation): local
    // enforcement is immediate; the external authority is attempted +
    // verified, and the response never claims more than the server confirmed.
    revoke: (payload: { invite_id?: string; grant_id?: string }) =>
      apiRequest<{
        ok: boolean
        status: 'revoked' | 'revoked_local_only'
        invite: { id: string; status: string }
        grant_id: string
        local_enforcement: boolean
        authority_state: 'confirmed' | 'unconfirmed' | 'unreachable' | 'not_configured'
        idempotent: boolean
        correlation_id: string
        message: string
      }>('/api/access/revoke', { method: 'POST', body: payload }),
    expire: (payload: { project_id: string; invite_id: string }) =>
      apiRequest<{ ok: true; dry_run: boolean; message: string; invite: { id: string; status: string } }>(
        '/api/access/expire', { method: 'POST', body: payload },
      ),
    // Commandery Secure Portal — MEMBER-facing redeem + verify-session proxies.
    // Both return HTTP 200 for every expected outcome (redeemed/denied/not_live/
    // etc.), so callers read `state` rather than catching. NEVER carry a session
    // token/binding/hash/cookie — the browser-bound session lives in an httpOnly
    // cookie minted server-side. The access code goes in the body, NEVER a URL.
    // See routes/Commandery/commanderyAccess.ts.
    // `code` is optional: omitting it is a SIGNED-LINK redemption attempt —
    // the server may answer state:'code_required', which reveals the hidden
    // fallback-code step. The code always travels in the body, never a URL.
    redeem: (payload: { invite_id?: string; token?: string; code?: string }) =>
      apiRequest<{
        ok: boolean
        state: string
        redeemed?: boolean
        invite_id?: string
        expires_at?: string | null
        reason_public?: string
        message: string
      }>('/api/access/redeem', { method: 'POST', body: payload }),
    verifySession: (payload?: { invite_id?: string }) =>
      apiRequest<{
        ok: boolean
        state: string
        verified?: boolean
        invite_id?: string
        session?: { status: string; expires_at: string | null }
        account_bootstrapped?: boolean
        account_state?: string
        reason_public?: string
        message: string
      }>('/api/access/verify-session', { method: 'POST', body: payload ?? {} }),
    // Recipient NON-CONSUMING resolve — validates a secure invitation link so the
    // recipient portal can show "Preparing your secure workspace" WITHOUT redeeming
    // it. Returns HTTP 200 for every outcome; callers read `status`. Carries only
    // recipient-safe display fields — never a token/hash/session. The link token
    // goes in the body, NEVER logged or stored. See routes/SecureAccess/recipient.
    resolve: (payload: { token?: string; invite_id?: string }) =>
      apiRequest<{
        ok: boolean
        status: string
        invitation_state: string
        ui_state: string
        display_project_name?: string | null
        display_inviter_name?: string | null
        display_purpose?: string | null
        expires_at?: string | null
        nda_required?: boolean
        notification_preferences_required?: boolean
        account_mode?: string | null
        fallback_available?: boolean
        next_step: string
        message: string
        correlation_id: string
      }>('/api/access/resolve', { method: 'POST', body: payload }),
    // ── Owner recipient-access controls (End User Portal Activation) ──
    // Grants are the project-scoped invite bindings; server truth is returned
    // after every mutation (no optimistic success). Never carries a code/hash.
    grants: (scope: { project_id?: string; portal_id?: string }) =>
      apiRequest<{ ok: boolean; grants: AccessGrantView[] }>('/api/access/grants', { query: scope }),
    grantAction: (payload: { action: 'suspend' | 'restore'; grant_id: string }) =>
      apiRequest<{ ok: boolean; grant: AccessGrantView; local_enforcement: boolean; idempotent: boolean }>(
        '/api/access/grants',
        { method: 'POST', body: payload },
      ),
  },

  // ── Recipient portal session (public; authorized by the sealed httpOnly
  // session cookie — the browser sends NO ids and holds NO secrets). ──
  portalSession: {
    manifest: () =>
      apiRequest<PortalSessionManifestResponse>('/api/portal/session/manifest'),
    module: (key: string) =>
      apiRequest<{
        ok: boolean
        state?: string
        module?: { key: string; label: string; state: string }
        available?: boolean
        content?: {
          name?: string
          summary?: string | null
          modules?: Array<{ key: string; label: string; state: string }>
          updates?: Array<{ id: string; body: string; published_at: string }>
        }
        message?: string
      }>('/api/portal/session/module', { query: { key } }),
    onboarding: (
      payload:
        | { step: 'nda'; accept: true }
        | { step: 'preferences'; portal_notifications?: boolean; email_updates?: boolean },
    ) =>
      apiRequest<PortalSessionManifestResponse>('/api/portal/session/onboarding', {
        method: 'POST',
        body: payload,
      }),
  },

  // Square Command Center (/square/command-center) — operator cockpit.
  // Every route is server-side, admin-gated (minLevel 80), and fails
  // closed. Writes link an Olivium receipt. Access codes are hash-at-rest:
  // the one-time plaintext is returned exactly once by accessCodes.create
  // and never again — no route ever returns code_hash or plaintext in a list.
  commandCenter: {
    overview: () =>
      apiRequest<{
        ok: true
        overview: {
          access_codes: { active: number; claimed: number; revoked: number; expired: number; total: number }
          invites: { org: { pending: number; total: number }; project: { pending: number; total: number } }
          sessions: { active: number; total: number }
          receipts: { recent: number }
          providers: {
            secure_share: { available: boolean; note: string }
            secure_shield: { available: boolean; note: string }
            controlled_calls: { available: boolean; note: string }
          }
          generated_at: string
        }
      }>('/api/square/command-center/overview', { method: 'GET' }),

    access: () =>
      apiRequest<{
        ok: true
        codes: CommandCenterAccessCode[]
        summary: { active: number; claimed: number; revoked: number; expired: number; total: number }
      }>('/api/square/command-center/access', { method: 'GET' }),

    accessCodes: {
      // Returns the one-time plaintext access_code ONCE. It is never shown
      // again and never stored in the clear (sha256_hash_only).
      create: (payload: {
        label: string
        project_id?: string | null
        audience?: 'operator' | 'team' | 'client'
        role?: 'operator' | 'client_owner' | 'client_collaborator' | 'viewer'
        max_uses?: number
        expires_in?: '1h' | '24h' | '7d' | '30d' | 'never'
        target_type?: AccessCodeTargetType
        target_id?: string
        target_label?: string
      }) =>
        apiRequest<{
          ok: true
          code: CommandCenterAccessCode
          access_code: string
          code_reveal: 'one_time'
          code_storage: 'sha256_hash_only'
          message: string
        }>('/api/square/command-center/access-codes', { method: 'POST', body: payload }),
      revoke: (payload: { code_id: string; reason?: string }) =>
        apiRequest<{ ok: true; code: CommandCenterAccessCode }>(
          '/api/square/command-center/access-codes/revoke', { method: 'POST', body: payload },
        ),
      expire: (payload: { code_id: string }) =>
        apiRequest<{ ok: true; code: CommandCenterAccessCode }>(
          '/api/square/command-center/access-codes/expire', { method: 'POST', body: payload },
        ),
      // Redeem a code (authenticated). Returns the granted scope; marks the
      // code claimed/consumed. Fails closed on invalid/expired/revoked codes.
      claim: (payload: { code: string }) =>
        apiRequest<{
          ok: true
          claimed: boolean
          grant: { audience: string; role: string; project_id: string | null } | null
          target: { type: string; id: string | null; label: string | null; destination: string | null } | null
          message: string
        }>('/api/square/command-center/access-codes/claim', { method: 'POST', body: payload }),
    },

    invites: {
      // Real invite creation against the existing sq_* invite tables.
      // scope 'project' → sq_project_access_invites, scope 'org' → sq_membership_invites.
      create: (payload: {
        scope: 'project' | 'org'
        email: string
        project_id?: string
        org_id?: string
        role?: string
        expires_in?: '24h' | '7d' | '30d'
      }) =>
        apiRequest<{ ok: true; invite: CommandCenterInvite }>(
          '/api/square/command-center/invites', { method: 'POST', body: payload },
        ),
      revoke: (payload: { scope: 'project' | 'org'; invite_id: string }) =>
        apiRequest<{ ok: true; invite: CommandCenterInvite }>(
          '/api/square/command-center/invites/revoke', { method: 'POST', body: payload },
        ),
      expire: (payload: { scope: 'project' | 'org'; invite_id: string }) =>
        apiRequest<{ ok: true; invite: CommandCenterInvite }>(
          '/api/square/command-center/invites/expire', { method: 'POST', body: payload },
        ),
    },

    receipts: (q?: { limit?: number }) =>
      apiRequest<{ ok: true; receipts: CommandCenterReceipt[] }>(
        '/api/square/command-center/receipts', { method: 'GET', query: { limit: q?.limit } },
      ),

    permissions: {
      list: () =>
        apiRequest<{
          ok: true
          roles: { id: string; name: string; level: number; permissions: string[]; description: string | null }[]
          overrides: CommandCenterPermissionOverride[]
        }>('/api/square/command-center/permissions', { method: 'GET' }),
      // Grant or deny a per-user permission override (sq_permission_overrides).
      set: (payload: {
        user_id: string
        org_id: string
        permission: string
        granted: boolean
        resource_type?: string | null
        resource_id?: string | null
        reason?: string
      }) =>
        apiRequest<{ ok: true; override: CommandCenterPermissionOverride }>(
          '/api/square/command-center/permissions', { method: 'POST', body: payload },
        ),
    },

    sessions: {
      list: () =>
        apiRequest<{ ok: true; sessions: CommandCenterSession[] }>(
          '/api/square/command-center/sessions', { method: 'GET' },
        ),
      revoke: (payload: { session_id: string; reason: string }) =>
        apiRequest<{ ok: true; session_id: string }>(
          '/api/square/command-center/sessions/revoke', { method: 'POST', body: payload },
        ),
      lock: (payload: { session_id: string; reason: string }) =>
        apiRequest<{ ok: true; session_id: string }>(
          '/api/square/command-center/sessions/lock', { method: 'POST', body: payload },
        ),
    },

    // Controlled session calls (Daily-powered) — not built yet. The route
    // always fails closed with a stable code until a provider is configured.
    controlledCalls: {
      create: (payload: { project_id?: string; label?: string }) =>
        apiRequest<{ ok: false; available: false; reason: string; message: string }>(
          '/api/square/command-center/controlled-calls', { method: 'POST', body: payload },
        ),
    },

    // Build Now Execute Layer — post a job, watch it move through an
    // IDE-style status timeline, approve gates, review proof and
    // receipts. This layer records job state; it does not itself
    // execute anything — no agent runtime is wired to advance a job
    // automatically.
    jobs: {
      list: (q?: { state?: string }) =>
        apiRequest<{ ok: true; jobs: PostJob[] }>(
          '/api/square/command-center/jobs', { method: 'GET', query: q },
        ),
      create: (payload: {
        title: string
        prompt: string
        job_type?: string
        target_surface: string
        deadline_mode?: 'build_now' | 'schedule_later' | 'deadline_by'
        requested_due_at?: string
        approval_gate?: string
        project_id?: string
        workspace_label?: string
        proof_required?: string
        receipt_required?: boolean
        // Creative extension — persisted to sq_design_job_details; the
        // execution engine plans its microjobs from these.
        requested_outputs?: string[]
        destination_scope?: 'draft_only' | 'internal_marketing' | 'external_delivery' | 'publish_or_blast'
        brand_context?: Record<string, unknown>
      }) =>
        apiRequest<{ ok: true; job: PostJob }>(
          '/api/square/command-center/jobs', { method: 'POST', body: payload },
        ),
      get: (job_id: string) =>
        apiRequest<PostJobDetailResponse>(
          '/api/square/command-center/jobs/get', { method: 'GET', query: { job_id } },
        ),
      postStatusEvent: (payload: {
        job_id: string
        message: string
        event_code?: string
        advance_to_state?: string
        progress_percent?: number
        blocker_class?: string
        evidence_class?: string
      }) =>
        apiRequest<{ ok: true; event: PostJobStatusEvent; state: string }>(
          '/api/square/command-center/jobs/status-event', { method: 'POST', body: payload },
        ),
      /** job_id XOR meeting_id — meeting-originated approvals (Conference
       *  Room) pass meeting_id and the server resolves/creates the shadow
       *  job itself; see request-approval.ts. */
      requestApproval: (payload: { job_id?: string; meeting_id?: string; approval_gate: string; required_by?: string; run_id?: string }) =>
        apiRequest<{ ok: true; approval: PostJobApproval }>(
          '/api/square/command-center/jobs/request-approval', { method: 'POST', body: payload },
        ),
      decideApproval: (payload: { approval_id: string; decision: 'approved' | 'rejected'; decision_note?: string }) =>
        apiRequest<{ ok: true; approval_id: string; decision: string }>(
          '/api/square/command-center/jobs/approve', { method: 'POST', body: payload },
        ),
      /** Every approval raised inside a given Conference Room meeting —
       *  see api/square/command-center/jobs/approvals-for-meeting.ts. */
      approvalsForMeeting: (meeting_id: string) =>
        apiRequest<{ ok: true; approvals: PostJobApproval[] }>(
          '/api/square/command-center/jobs/approvals-for-meeting', { method: 'GET', query: { meeting_id } },
        ),
    },

    // Design Creation Execution Engine — the agent runtime that executes
    // posted jobs for real (composition, SVG rendering, quality checks),
    // halting at approval gates. Runs in-process per request; no cron.
    engine: {
      run: (payload: { job_id: string; max_steps?: number }) =>
        apiRequest<{ ok: boolean; result: EngineRunResult }>(
          '/api/square/command-center/engine/run', { method: 'POST', body: payload },
        ),
      tick: (payload: { job_id: string }) =>
        apiRequest<{ ok: boolean; result: EngineRunResult }>(
          '/api/square/command-center/engine/tick', { method: 'POST', body: payload },
        ),
    },

    // Tester/client feedback — the input side of the v10 qcb.* metrics
    // that cannot be derived from system rows. Non-admins list only
    // their own feedback.
    feedback: {
      list: () =>
        apiRequest<{ ok: true; feedback: Array<{ id: string; job_id: string | null; metric_id: string; rating: number | null; comment: string | null; created_by: string; created_at: string }> }>(
          '/api/square/command-center/feedback', { method: 'GET' },
        ),
      record: (payload: { job_id?: string; metric_id?: string; rating?: number; comment?: string }) =>
        apiRequest<{ ok: true; feedback: { id: string } }>(
          '/api/square/command-center/feedback', { method: 'POST', body: payload },
        ),
    },

    // Saved job recipes — replayable templates ("your favorite, saved").
    recipes: {
      list: () =>
        apiRequest<{ ok: true; recipes: JobRecipe[]; available?: boolean; reason?: string; migration?: string; message?: string }>(
          '/api/square/command-center/recipes', { method: 'GET' },
        ),
      saveFromJob: (payload: { job_id: string; title?: string; description?: string }) =>
        apiRequest<{ ok: true; recipe: JobRecipe }>(
          '/api/square/command-center/recipes', { method: 'POST', body: payload },
        ),
      replay: (payload: { recipe_id: string; title?: string; prompt?: string; autorun?: boolean }) =>
        apiRequest<{ ok: true; job_id: string; engine: EngineRunResult | null }>(
          '/api/square/command-center/recipes/replay', { method: 'POST', body: payload },
        ),
    },

    // QCAL-01 Quantum Calendar Agent — plain-language planning requests,
    // scored candidate schedules, owner/client scope kept strictly
    // separate. Nothing here writes an external calendar, sends a
    // notification, or spends provider credits.
    calendar: {
      plan: (payload: {
        request_text: string
        project_id?: string
        window_type?: CalendarPlan['window_type']
        tasks?: Array<{ id: string; title: string; importance: number; urgency: number; estimatedMinutes: number; deadline?: string; dependencies?: string[]; requiresApproval?: boolean }>
      }) =>
        apiRequest<{ ok: true; plan: CalendarPlan; result: unknown }>(
          '/api/square/command-center/calendar/plan', { method: 'POST', body: payload },
        ),
      list: () =>
        apiRequest<{ ok: true; plans: CalendarPlan[] }>(
          '/api/square/command-center/calendar/plan', { method: 'GET' },
        ),
      get: (plan_id: string) =>
        apiRequest<{ ok: true; plan: CalendarPlan; events: CalendarPlanEvent[]; handoffs: CalendarHandoff[] }>(
          '/api/square/command-center/calendar/get', { method: 'GET', query: { plan_id } },
        ),
      accept: (plan_id: string) =>
        apiRequest<{ ok: true; handoff: CalendarHandoff }>(
          '/api/square/command-center/calendar/accept', { method: 'POST', body: { plan_id } },
        ),
      reject: (plan_id: string, note?: string) =>
        apiRequest<{ ok: true; learning_notes: unknown[] }>(
          '/api/square/command-center/calendar/reject', { method: 'POST', body: { plan_id, note } },
        ),
      feedback: (payload: { plan_id: string; accepted?: boolean; completed?: boolean; rescheduled?: boolean; overload_reported?: boolean; quality_score?: number }) =>
        apiRequest<{ ok: true; learning_notes: unknown[] }>(
          '/api/square/command-center/calendar/feedback', { method: 'POST', body: payload },
        ),
      windows: () =>
        apiRequest<{ ok: true; windows: CalendarWindowInfo[] }>(
          '/api/square/command-center/calendar/windows', { method: 'GET' },
        ),
    },

    // Daily Meeting Mode (v17) — controlled Daily rooms, scoped
    // short-lived join tokens (never persisted), events + receipts.
    // Server-mediated: the browser never talks to Daily's REST API and
    // never sees DAILY_API_KEY.
    meetings: {
      list: () =>
        apiRequest<{ ok: true; meetings: Meeting[]; provider_configured: boolean }>(
          '/api/square/daily/meetings', { method: 'GET' },
        ),
      create: (payload: {
        title: string
        meeting_mode: string
        duration_minutes?: number
        max_participants?: number
        project_id?: string
        job_id?: string
        context_type?: string
        context_id?: string
      }) =>
        apiRequest<{ ok: boolean; meeting?: Meeting; reason?: string; required_approval?: string | null; message?: string }>(
          '/api/square/daily/meetings', { method: 'POST', body: payload },
        ),
      join: (meeting_id: string, participant_role?: string, display_name?: string) =>
        apiRequest<MeetingJoinResult | { ok: false; reason: string }>(
          '/api/square/daily/meetings/join', { method: 'POST', body: { meeting_id, participant_role, display_name } },
        ),
      cancel: (meeting_id: string, reason?: string) =>
        apiRequest<{ ok: true; meeting_id: string; status: 'cancelled' }>(
          '/api/square/daily/meetings/cancel', { method: 'POST', body: { meeting_id, reason } },
        ),
      status: () =>
        apiRequest<DailyStatus>('/api/square/daily/status', { method: 'GET' }),

      // Waiting room + Meeting Concierge (mtg-01): the agent that keeps
      // guests engaged (agenda collection, timeliness relays) until the
      // host joins and admits them. Speaks only inside this waiting room.
      waitingRoom: {
        enter: (meeting_id: string, display_name?: string) =>
          apiRequest<{ ok: boolean; session?: WaitingSession; resumed?: boolean; reason?: string }>(
            '/api/square/daily/waiting-room/enter', { method: 'POST', body: { meeting_id, display_name } },
          ),
        messages: (session_id: string) =>
          apiRequest<{ ok: true; session: WaitingSession; messages: WaitingMessage[] }>(
            '/api/square/daily/waiting-room/messages', { method: 'GET', query: { session_id } },
          ),
        send: (session_id: string, body: string) =>
          apiRequest<{ ok: true; messages: WaitingMessage[] }>(
            '/api/square/daily/waiting-room/messages', { method: 'POST', body: { session_id, body } },
          ),
        sessions: (meeting_id: string) =>
          apiRequest<{ ok: true; sessions: WaitingSession[] }>(
            '/api/square/daily/waiting-room/sessions', { method: 'GET', query: { meeting_id } },
          ),
        hostUpdate: (meeting_id: string, payload: { note?: string; ask_guests?: string }) =>
          apiRequest<{ ok: true; sessions_reached: number }>(
            '/api/square/daily/waiting-room/host-update', { method: 'POST', body: { meeting_id, ...payload } },
          ),
        admit: (session_id: string) =>
          apiRequest<{ ok: true; session_id: string; status: 'admitted' }>(
            '/api/square/daily/waiting-room/admit', { method: 'POST', body: { session_id } },
          ),
      },
    },

    // Route Limit, Usage, and Concurrency System (SCFM-11) — read-only
    // status surface today; policy writes are owner/admin-only server-side.
    usage: {
      policies: () =>
        apiRequest<{ ok: true; policies: UsageRoutePolicy[] }>(
          '/api/square/command-center/usage/policies', { method: 'GET' },
        ),
      check: (agent_id: string, route_key: string, model_tier?: UsageRoutePolicy['model_tier']) =>
        apiRequest<UsageDecision>(
          '/api/square/command-center/usage/check', { method: 'GET', query: { agent_id, route_key, model_tier } },
        ),
    },

    // Agent Control Center (SCFM-07) — the governed-agent management
    // portal. Pausing is real: paused agents are refused by their
    // runtime lanes server-side.
    agents: {
      list: () =>
        apiRequest<{ ok: true; agents: RegistryAgent[] }>(
          '/api/square/command-center/agents', { method: 'GET' },
        ),
      get: (agent_id: string) =>
        apiRequest<{
          ok: true
          agent: RegistryAgent
          skills: Array<Record<string, unknown>>
          routes: Array<Record<string, unknown>>
          usage_policies: UsageRoutePolicy[]
          recent_usage: Array<Record<string, unknown>>
          recent_runs: Array<Record<string, unknown>>
        }>('/api/square/command-center/agents/get', { method: 'GET', query: { agent_id } }),
      control: (agent_id: string, action: 'pause' | 'activate') =>
        apiRequest<{ ok: true; agent_id: string; status: string; already: boolean }>(
          '/api/square/command-center/agents/control', { method: 'POST', body: { agent_id, action } },
        ),
    },

    // Modular Project Board (v24) — boards, groups, cards, timeline.
    // send_to_job routes into the real Build Now Execute spine.
    boards: {
      list: () =>
        apiRequest<{ ok: true; boards: Array<{ id: string; title: string; created_at: string }> }>(
          '/api/square/command-center/boards', { method: 'GET' },
        ),
      create: (title: string, description?: string) =>
        apiRequest<{ ok: true; board: { id: string; title: string; created_at: string } }>(
          '/api/square/command-center/boards', { method: 'POST', body: { title, description } },
        ),
      get: (board_id: string) =>
        apiRequest<{
          ok: true
          board: { id: string; title: string; created_at: string }
          groups: Array<{ id: string; title: string; position: number }>
          items: Array<{ id: string; group_id: string | null; title: string; description: string | null; status: string; priority: string; agent_manager: string | null; linked_job_id: string | null }>
          events: Array<{ id: string; event_type: string; event_payload: Record<string, unknown>; created_at: string }>
        }>('/api/square/command-center/boards/get', { method: 'GET', query: { board_id } }),
      itemAction: (payload: Record<string, unknown>) =>
        apiRequest<{ ok: boolean; item?: Record<string, unknown>; item_id?: string; job_id?: string; reason?: string }>(
          '/api/square/command-center/boards/items', { method: 'POST', body: payload },
        ),
    },
  },

  // Agent Workspace Full Control (v23) — threads with agents, tasks,
  // and PR-style action requests. Agents prepare; approval (minLevel 80,
  // server-side) executes through the owning gate. Nothing sends
  // externally.
  agentWorkspace: {
    workspaces: {
      list: () =>
        apiRequest<{ ok: true; workspaces: Array<{ id: string; name: string; created_at: string }> }>(
          '/api/square/agent-workspace/workspaces', { method: 'GET' },
        ),
      create: (name: string) =>
        apiRequest<{ ok: true; workspace: { id: string; name: string; created_at: string } }>(
          '/api/square/agent-workspace/workspaces', { method: 'POST', body: { name } },
        ),
    },
    threads: {
      list: (workspace_id: string) =>
        apiRequest<{ ok: true; threads: Array<{ id: string; title: string; thread_type: string; agent_ids: string[] }> }>(
          '/api/square/agent-workspace/threads', { method: 'GET', query: { workspace_id } },
        ),
      create: (workspace_id: string, title: string, agent_ids: string[]) =>
        apiRequest<{ ok: true; thread: { id: string; title: string; thread_type: string; agent_ids: string[] } }>(
          '/api/square/agent-workspace/threads', { method: 'POST', body: { workspace_id, title, agent_ids } },
        ),
    },
    messages: {
      list: (thread_id: string) =>
        apiRequest<{ ok: true; messages: Array<Record<string, unknown>> }>(
          '/api/square/agent-workspace/messages', { method: 'GET', query: { thread_id } },
        ),
      send: (thread_id: string, body: string) =>
        apiRequest<{ ok: true; messages: Array<Record<string, unknown>> }>(
          '/api/square/agent-workspace/messages', { method: 'POST', body: { thread_id, body } },
        ),
    },
    tasks: {
      list: (workspace_id: string) =>
        apiRequest<{ ok: true; tasks: Array<Record<string, unknown>> }>(
          '/api/square/agent-workspace/tasks', { method: 'GET', query: { workspace_id } },
        ),
      create: (payload: { workspace_id: string; title: string; assigned_agent_ids: string[]; description?: string; priority?: string }) =>
        apiRequest<{ ok: true; task: Record<string, unknown> }>(
          '/api/square/agent-workspace/tasks', { method: 'POST', body: payload },
        ),
    },
    actionRequests: {
      list: (workspace_id: string) =>
        apiRequest<{ ok: true; action_requests: Array<Record<string, unknown>> }>(
          '/api/square/agent-workspace/action-requests', { method: 'GET', query: { workspace_id } },
        ),
      create: (payload: Record<string, unknown>) =>
        apiRequest<{ ok: boolean; action_request?: Record<string, unknown>; reason?: string; unresolved?: Array<Record<string, unknown>> }>(
          '/api/square/agent-workspace/action-requests', { method: 'POST', body: payload },
        ),
    },
    approve: (request_id: string, decision: 'approved' | 'rejected' | 'revision_requested', note?: string) =>
      apiRequest<{ ok: true; request_id: string; approval_status: string; execution_status?: string; message?: string; result?: Record<string, unknown> }>(
        '/api/square/agent-workspace/approve', { method: 'POST', body: { request_id, decision, note } },
      ),
  },

  // Functional Agent Runtime — the governed supervised loop: create a task,
  // drive it, watch its event stream, approve gated writes. Registry truth,
  // durable, honest (no fixture output; provider failures surface as-is).
  agentRuntime: {
    agents: {
      list: () =>
        apiRequest<{ ok: true; provider_configured: boolean; agents: Array<Record<string, unknown>> }>(
          '/api/square/agent-runtime/agents', { method: 'GET' },
        ),
      get: (agent_id: string) =>
        apiRequest<{ ok: true; agent: Record<string, unknown>; tools: Array<Record<string, unknown>> }>(
          '/api/square/agent-runtime/agents', { method: 'GET', query: { agent_id } }),
    },
    tasks: {
      create: (payload: { agent_id: string; title: string; instruction: string; project_id?: string; execution_mode?: string; expected_output?: string; approved_tool_scopes?: string[] }) =>
        apiRequest<{ ok: true; task_id: string; correlation_id: string }>(
          '/api/square/agent-runtime/tasks', { method: 'POST', body: payload },
        ),
      get: (task_id: string) =>
        apiRequest<{ ok: true; task: Record<string, unknown>; tool_calls: Array<Record<string, unknown>>; pending_approval: Record<string, unknown> | null }>(
          '/api/square/agent-runtime/tasks', { method: 'GET', query: { task_id } }),
      listByProject: (project_id: string) =>
        apiRequest<{ ok: true; tasks: Array<Record<string, unknown>> }>(
          '/api/square/agent-runtime/tasks', { method: 'GET', query: { project_id } }),
      mine: () =>
        apiRequest<{ ok: true; tasks: Array<Record<string, unknown>> }>(
          '/api/square/agent-runtime/tasks', { method: 'GET' }),
    },
    events: (task_id: string, after_seq?: number) =>
      apiRequest<{ ok: true; runtime_status: string; events: Array<Record<string, unknown>> }>(
        '/api/square/agent-runtime/events', { method: 'GET', query: after_seq != null ? { task_id, after_seq: String(after_seq) } : { task_id } }),
    tick: (task_id: string, max_ticks?: number) =>
      apiRequest<{ ok: boolean; runtime_status: string; halted: string; message: string }>(
        '/api/square/agent-runtime/tick', { method: 'POST', body: max_ticks != null ? { task_id, max_ticks } : { task_id } }),
    control: (task_id: string, action: 'pause' | 'resume' | 'cancel' | 'retry') =>
      apiRequest<{ ok: boolean; action: string; runtime_status: string }>(
        '/api/square/agent-runtime/control', { method: 'POST', body: { task_id, action } }),
    approve: (action_request_id: string, decision: 'approved' | 'rejected' | 'revision_requested', note?: string) =>
      apiRequest<{ ok: boolean; approval_status: string; reason?: string }>(
        '/api/square/agent-runtime/approve', { method: 'POST', body: { action_request_id, decision, note } }),
    health: () =>
      apiRequest<{ ok: true; overall: string; checks: Record<string, { state: string; detail: string }> }>(
        '/api/square/agent-runtime/health', { method: 'GET' }),
    recommend: (instruction: string, context?: Record<string, unknown>, explicit_agent?: string) =>
      apiRequest<{
        ok: true
        provider_configured: boolean
        explicit: boolean
        context: Record<string, unknown>
        recommended: Record<string, unknown> | null
        alternatives: Array<Record<string, unknown>>
      }>('/api/square/agent-runtime/recommend', { method: 'POST', body: { instruction, context, explicit_agent } }),
  },

  // Team Workspace (v26) — native internal-only collaboration. Agent
  // drafts are approval-gated: invisible until the operator approves.
  teamWorkspace: {
    spaces: () =>
      apiRequest<{ ok: true; spaces: Array<{ id: string; name: string }>; channels: Array<{ id: string; space_id: string; name: string }> }>(
        '/api/square/team-workspace/spaces', { method: 'GET' },
      ),
    createSpace: (name: string) =>
      apiRequest<{ ok: true; space: { id: string; name: string } }>(
        '/api/square/team-workspace/spaces', { method: 'POST', body: { name } },
      ),
    threads: (channel_id: string) =>
      apiRequest<{ ok: true; threads: Array<{ id: string; title: string; status: string }> }>(
        '/api/square/team-workspace/threads', { method: 'GET', query: { channel_id } },
      ),
    createThread: (channel_id: string, title: string) =>
      apiRequest<{ ok: true; thread: { id: string; title: string; status: string } }>(
        '/api/square/team-workspace/threads', { method: 'POST', body: { channel_id, title } },
      ),
    messages: (thread_id: string) =>
      apiRequest<{ ok: true; thread: Record<string, unknown>; messages: Array<Record<string, unknown>>; pending_drafts: Array<Record<string, unknown>>; pins: Array<Record<string, unknown>> }>(
        '/api/square/team-workspace/messages', { method: 'GET', query: { thread_id } },
      ),
    messageAction: (payload: Record<string, unknown>) =>
      apiRequest<{ ok: boolean; message?: Record<string, unknown> }>(
        '/api/square/team-workspace/messages', { method: 'POST', body: payload },
      ),
    requestDraft: (thread_id: string, agent_id: string, instruction: string) =>
      apiRequest<{ ok: boolean; draft?: Record<string, unknown>; reason?: string; message?: string }>(
        '/api/square/team-workspace/agent-draft', { method: 'POST', body: { action: 'request', thread_id, agent_id, instruction } },
      ),
    decideDraft: (message_id: string, decision: 'approved' | 'rejected') =>
      apiRequest<{ ok: true; message_id: string; draft_status: string }>(
        '/api/square/team-workspace/agent-draft', { method: 'POST', body: { action: 'decide', message_id, decision } },
      ),
  },

  // Work Collaboration Hub (v28) — routing surface over existing lanes.
  // Prepared actions land in the v23 approval queue or route the
  // operator to the owning surface; no external provider dispatch.
  workCollaboration: {
    hub: () =>
      apiRequest<{ ok: true; hub: { title: string; modules: Array<{ key: string; label: string; owningLane: string; routePath: string; summary: string; status: string; statusReason: string }>; warnings: string[] } }>(
        '/api/square/work-collaboration/hub', { method: 'GET' },
      ),
    prepare: (payload: Record<string, unknown>) =>
      apiRequest<{ ok: boolean; prepared?: { kind: string; title: string; status: string; route_path?: string; action_request_id?: string; note?: string } }>(
        '/api/square/work-collaboration/hub', { method: 'POST', body: { action: 'prepare', ...payload } },
      ),
  },

  // Quam Calendar Live Workspace — QCAL-01 planning plus the live
  // Google Calendar bridge: reads, drafts, operator-approved commits
  // (the stack's first controlled live external write), receipts per
  // provider call.
  quamCalendar: {
    status: () =>
      apiRequest<{ ok: true; connected: boolean; reason: string | null; google_email: string | null; granted_scopes: string[]; requested_calendar_scopes: string[]; write_scopes: string[]; preferences: Record<string, unknown> | null }>(
        '/api/square/command-center/quam-calendar/status', { method: 'GET' },
      ),
    connectCalendar: () =>
      apiRequest<{ ok: boolean; auth_url?: string; reason?: string; scopes?: string[] }>(
        '/api/square/google/oauth/start-calendar', { method: 'GET' },
      ),
    events: (query?: Record<string, string>) =>
      apiRequest<{ ok: boolean; google_status: Record<string, unknown>; google_events: Array<Record<string, unknown>>; drafts: Array<Record<string, unknown>>; quam_plans: Array<Record<string, unknown>> }>(
        '/api/square/command-center/quam-calendar/events', { method: 'GET', query: query ?? {} },
      ),
    freebusy: (payload: Record<string, unknown>) =>
      apiRequest<{ ok: boolean; freebusy?: Record<string, unknown>; reason?: string }>(
        '/api/square/command-center/quam-calendar/freebusy', { method: 'POST', body: payload },
      ),
    plan: (payload: Record<string, unknown>) =>
      apiRequest<{ ok: boolean; result?: Record<string, unknown>; reason?: string; message?: string }>(
        '/api/square/command-center/quam-calendar/plan', { method: 'POST', body: payload },
      ),
    draftEvent: (payload: Record<string, unknown>) =>
      apiRequest<{ ok: boolean; draft?: Record<string, unknown>; reason?: string; message?: string }>(
        '/api/square/command-center/quam-calendar/draft-event', { method: 'POST', body: payload },
      ),
    approveEvent: (draft_id: string, note?: string) =>
      apiRequest<{ ok: boolean; draft?: Record<string, unknown>; reason?: string }>(
        '/api/square/command-center/quam-calendar/approve-event', { method: 'POST', body: { draft_id, note } },
      ),
    commitEvent: (draft_id: string) =>
      apiRequest<{ ok: boolean; draft?: Record<string, unknown>; google_event?: Record<string, unknown>; reason?: string; message?: string; missing_scopes?: string[] }>(
        '/api/square/command-center/quam-calendar/commit-event', { method: 'POST', body: { draft_id } },
      ),
    createMeeting: (draft_id: string) =>
      apiRequest<{ ok: boolean; meeting?: Record<string, unknown>; reason?: string }>(
        '/api/square/command-center/quam-calendar/meeting', { method: 'POST', body: { draft_id } },
      ),
    // Scheduler (qcal-sched v2 slice): NL request → scored candidates →
    // selecting one creates a draft through the same approval gate.
    schedulerRequests: () =>
      apiRequest<{ ok: true; requests: Array<Record<string, unknown> & { candidates: Array<Record<string, unknown>> }> }>(
        '/api/square/command-center/calendar-agent/request', { method: 'GET' },
      ),
    schedulerRequest: (payload: Record<string, unknown>) =>
      apiRequest<{ ok: boolean; request?: Record<string, unknown>; candidates?: Array<Record<string, unknown>>; reason?: string; message?: string }>(
        '/api/square/command-center/calendar-agent/request', { method: 'POST', body: payload },
      ),
    approveCandidate: (candidate_id: string) =>
      apiRequest<{ ok: boolean; draft?: Record<string, unknown>; reason?: string }>(
        '/api/square/command-center/calendar-agent/approve-candidate', { method: 'POST', body: { candidate_id } },
      ),
    // Workspace items — notes / links / file refs / messages anchored to
    // calendar work (sq_calendar_workspace_items; migration 20260711000001
    // is source-ready — the route reports backend_not_migrated until it is
    // applied with operator approval).
    workspaceItems: {
      list: (query: { anchor_kind?: string; anchor_id?: string; month?: string }) =>
        apiRequest<{ ok: true; items: CalendarWorkspaceItem[] }>(
          '/api/square/command-center/quam-calendar/workspace-items', { method: 'GET', query },
        ),
      create: (payload: { anchor_kind: string; anchor_id: string; item_type: string; anchor_date?: string; title?: string; body?: string; url?: string; file_bucket?: string; file_path?: string; file_name?: string; metadata?: Record<string, unknown> }) =>
        apiRequest<{ ok: true; item: CalendarWorkspaceItem }>(
          '/api/square/command-center/quam-calendar/workspace-items', { method: 'POST', body: { action: 'create', ...payload } },
        ),
      update: (id: string, patch: { title?: string; body?: string; url?: string; metadata?: Record<string, unknown> }) =>
        apiRequest<{ ok: true; item: CalendarWorkspaceItem }>(
          '/api/square/command-center/quam-calendar/workspace-items', { method: 'POST', body: { action: 'update', id, ...patch } },
        ),
      remove: (id: string) =>
        apiRequest<{ ok: true; deleted: string }>(
          '/api/square/command-center/quam-calendar/workspace-items', { method: 'POST', body: { action: 'delete', id } },
        ),
    },
  },

  // Activation Doctor — read-only self-report of deployment, env,
  // database, Google redirect, and agent readiness. Admin-gated
  // (minLevel 80) server-side; never returns a secret value.
  activation: {
    status: () =>
      apiRequest<{ ok: true; activation: ActivationReport }>(
        '/api/square/activation/status', { method: 'GET' },
      ),
  },

  // Excel / OneDrive (Microsoft Graph) — presence-only status. Never a
  // live Graph call; setup-required until MICROSOFT_* env is set.
  integrations: {
    microsoftStatus: () =>
      apiRequest<{ ok: true; configured: boolean; connected: boolean; has_client_id: boolean; has_client_secret: boolean; has_redirect_uri: boolean; tenant_mode: string; note: string }>(
        '/api/square/integrations/microsoft/status', { method: 'GET' },
      ),
  },

  // PR Factory — GitHub REST proxy. Reports repo/branch/commit/open-PR
  // truth from GitHub; create is operator-approved. Fails closed to
  // github_not_connected without a server-side GITHUB_TOKEN.
  prFactory: {
    status: (branch?: string) =>
      apiRequest<
        | { ok: true; connected: true; repo: string; default_branch: string; target_branch: string; latest_commit: { sha: string; message: string; author: string | null; date: string | null } | null; open_prs: Array<{ number: number; title: string; head: string; base: string; url: string; draft: boolean }> }
        | { ok: true; connected: false; reason: string; repo: string; message: string }
      >('/api/square/command-center/pr-factory', { method: 'GET', query: branch ? { branch } : {} }),
    createPr: (payload: { head: string; base?: string; title: string; body?: string; draft?: boolean }) =>
      apiRequest<{ ok: boolean; created: boolean; pr?: { number: number; url: string; title: string }; reason?: string; message?: string }>(
        '/api/square/command-center/pr-factory', { method: 'POST', body: payload },
      ),
  },

  // Client Intake — sendable invite/receiver. Operator methods manage
  // invites + the review queue (authenticated); public methods are
  // called by the unauthenticated receiver form (no bearer token).
  intake: {
    invites: () =>
      apiRequest<{ ok: true; invites: Array<Record<string, unknown>> }>(
        '/api/square/command-center/intake-invites', { method: 'GET' },
      ),
    createInvite: (payload: {
      title: string
      invite_type?: 'website' | 'software' | 'presentation' | 'other' | 'custom'
      description?: string
      questions_json?: Array<Record<string, unknown>>
      audience?: string
      target_project_id?: string
      allow_attachments?: boolean
      expires_at?: string
    }) =>
      apiRequest<{ ok: true; invite: Record<string, unknown>; public_path: string }>(
        '/api/square/command-center/intake-invites', { method: 'POST', body: payload },
      ),
    revokeInvite: (invite_id: string) =>
      apiRequest<{ ok: boolean; invite_id?: string; status?: string }>(
        '/api/square/command-center/intake-invites', { method: 'POST', body: { action: 'revoke', invite_id } },
      ),
    submissions: () =>
      apiRequest<{ ok: true; submissions: Array<Record<string, unknown>> }>(
        '/api/square/command-center/intake-invites/submissions', { method: 'GET' },
      ),
    decideSubmission: (payload: { submission_id: string; decision: 'pending' | 'resolved' | 'closed' }) =>
      apiRequest<{ ok: boolean; submission_id?: string; status?: string }>(
        '/api/square/command-center/intake-invites/submissions', { method: 'POST', body: payload },
      ),
    convertToProject: (submission_id: string) =>
      apiRequest<{ ok: boolean; converted?: boolean; project?: { id: string; name: string; slug: string }; navigate?: string; message?: string; error?: string }>(
        '/api/square/command-center/intake-invites/submissions', { method: 'POST', body: { action: 'convert_to_project', submission_id } },
      ),
    // ATOMIC — create a real client + its project + the binding in one
    // transaction; returns the persisted quantum-sync receipt. Fail-closed
    // (server refuses without an active org and re-verifies the binding).
    createClientProject: (payload: {
      client_name: string
      project_name: string
      contact_email?: string
      contact_phone?: string
      project_type?: 'website' | 'software' | 'presentation' | 'operations' | 'legal' | 'marketing' | 'event' | 'other'
      project_slug?: string
      /** Operator-facing delivery stage (e.g. Mockup / MVP / Full Build); persisted to project metadata. */
      stage?: string
      /** Freeform notes; persisted to project metadata. */
      notes?: string
    }) =>
      apiRequest<{
        ok: true
        client: Record<string, unknown>
        project: Record<string, unknown>
        receipt: Record<string, unknown>
        sync: { client_created: boolean; project_created: boolean; binding_confirmed: boolean; receipt_written: boolean; checks: Array<{ key: string; label: string; status: string; id?: string }> }
        /** False when the client+project were sealed but the stage/notes metadata follow-up write failed. */
        metadata_persisted?: boolean
      }>(
        '/api/square/command-center/intake/create-client-project', { method: 'POST', body: payload },
      ),
    // The reuse directory backing the intake form's autocomplete dropdowns:
    // the operator's real, previously-saved clients + projects (owner- or
    // org-scoped server-side). Read-only.
    directory: () =>
      apiRequest<{
        ok: true
        scope: 'owner'
        clients: Array<{ id: string; name: string; email: string | null; phone: string | null }>
        projects: Array<{ id: string; name: string; clientName: string | null; stage: string | null; createdAt: string | null }>
      }>(
        '/api/square/command-center/intake/directory', { method: 'GET' },
      ),
    // PUBLIC — the receiver form. No auth; the invite_code is the credential.
    publicGet: (code: string) =>
      apiRequest<
        | { ok: true; invite: { invite_code: string; invite_type: string; title: string; description: string | null; questions: Array<Record<string, unknown>>; allow_attachments: boolean } }
        | { ok: false; reason: string }
      >('/api/square/intake/get', { method: 'GET', query: { code } }),
    publicSubmit: (payload: { code: string; name: string; email: string; description: string; answers?: Record<string, unknown>; attachments?: Array<{ name: string; size?: string; type?: string }> }) =>
      apiRequest<{ ok: boolean; submitted?: boolean; message?: string; reason?: string }>(
        '/api/square/intake/submit', { method: 'POST', body: payload },
      ),
  },

  // Google Workspace Vault Connector (v29 lane) — selected-source
  // intake. Server-mediated: tokens and the client secret never reach
  // the browser; read-only scopes; a receipt per provider call.
  google: {
    status: () =>
      apiRequest<Record<string, unknown> & {
        ok: true; configured: boolean; enabled: boolean; connected: boolean; google_email: string | null
        last_oauth: { status: string; layer: string | null; reason: string | null; at: string } | null
      }>(
        '/api/square/google/status', { method: 'GET' },
      ),
    oauthStart: () =>
      apiRequest<{ ok: boolean; auth_url?: string; reason?: string; message?: string }>(
        '/api/square/google/oauth/start', { method: 'GET' },
      ),
    driveList: (q?: string) =>
      apiRequest<{ ok: boolean; files?: Array<Record<string, unknown>>; reason?: string }>(
        '/api/square/google/drive/list', { method: 'GET', query: q ? { q } : {} },
      ),
    driveImport: (file_id: string) =>
      apiRequest<{ ok: boolean; item?: Record<string, unknown>; reason?: string }>(
        '/api/square/google/drive/import', { method: 'POST', body: { file_id } },
      ),
    gmailSearch: (q: string) =>
      apiRequest<{ ok: boolean; messages?: Array<Record<string, unknown>>; reason?: string }>(
        '/api/square/google/gmail/search', { method: 'POST', body: { q } },
      ),
    gmailImport: (message_id: string) =>
      apiRequest<{ ok: boolean; item?: Record<string, unknown>; reason?: string }>(
        '/api/square/google/gmail/import', { method: 'POST', body: { message_id } },
      ),
    docsImport: (document_id: string) =>
      apiRequest<{ ok: boolean; item?: Record<string, unknown>; reason?: string }>(
        '/api/square/google/docs/import', { method: 'POST', body: { document_id } },
      ),
    sendToLandingAi: (vault_item_id: string, run_type?: 'parse' | 'extract' | 'classify' | 'split') =>
      apiRequest<{ ok: boolean; run?: Record<string, unknown>; message?: string }>(
        '/api/square/google/vault/send-to-landingai', { method: 'POST', body: run_type ? { vault_item_id, run_type } : { vault_item_id } },
      ),
    landingaiDecide: (run_id: string, decision: 'authorize' | 'reject') =>
      apiRequest<{ ok: boolean; run_id?: string; status?: string; message?: string }>(
        '/api/square/google/vault/landingai-decide', { method: 'POST', body: { run_id, decision } },
      ),
    landingaiExecute: (run_id: string, schema?: Record<string, unknown>) =>
      apiRequest<{ ok: boolean; run_id?: string; status?: string; result?: Record<string, unknown>; message?: string }>(
        '/api/square/google/vault/landingai-execute', { method: 'POST', body: schema ? { run_id, schema } : { run_id } },
      ),
    landingaiReview: (run_id: string, decision: 'accepted' | 'rejected' | 'needs_rework', notes?: string) =>
      apiRequest<{ ok: boolean; run_id?: string; review_status?: string; message?: string }>(
        '/api/square/google/vault/landingai-review', { method: 'POST', body: notes ? { run_id, decision, notes } : { run_id, decision } },
      ),
    vaultItems: () =>
      apiRequest<{ ok: true; items: Array<Record<string, unknown>>; landingai_runs: Array<Record<string, unknown>>; suggestions: Array<Record<string, unknown>> }>(
        '/api/square/google/vault/items', { method: 'GET' },
      ),
    vaultAction: (payload: Record<string, unknown>) =>
      apiRequest<{ ok: boolean; suggestion?: Record<string, unknown>; status?: string; message?: string }>(
        '/api/square/google/vault/items', { method: 'POST', body: payload },
      ),
  },

  // Graphify run registry (v29 slice 3) — prepare / decide / register
  // artifacts. Graphify never auto-runs against private source; no
  // executor exists — the registry records intent, decisions, and
  // externally-produced outputs.
  graphify: {
    runs: () =>
      apiRequest<{ ok: true; runs: Array<Record<string, unknown>> }>(
        '/api/square/graphify/runs', { method: 'GET' },
      ),
    prepare: (payload: { source_kind: 'repo' | 'vault_item' | 'docs'; source_ref: string; purpose?: string }) =>
      apiRequest<{ ok: boolean; run?: Record<string, unknown>; message?: string }>(
        '/api/square/graphify/runs', { method: 'POST', body: payload },
      ),
    decide: (run_id: string, decision: 'authorize' | 'reject') =>
      apiRequest<{ ok: boolean; run_id?: string; status?: string; message?: string }>(
        '/api/square/graphify/decide', { method: 'POST', body: { run_id, decision } },
      ),
    registerArtifacts: (payload: { run_id: string; graph_html_ref?: string; graph_json_ref?: string; report_md_text?: string; outcome?: 'failed' }) =>
      apiRequest<{ ok: boolean; run_id?: string; status?: string; message?: string }>(
        '/api/square/graphify/artifacts', { method: 'POST', body: payload },
      ),
  },

  // Damages Project Workspace (v29 slice 4) — review-gated operator
  // work products. Every entry is born draft; acceptance is an
  // operator decision. No legal or medical conclusions.
  damages: {
    workspaces: () =>
      apiRequest<{ ok: true; workspaces: Array<Record<string, unknown>> }>(
        '/api/square/damages/workspaces', { method: 'GET' },
      ),
    createWorkspace: (payload: { title: string; project_ref?: string }) =>
      apiRequest<{ ok: boolean; workspace?: Record<string, unknown> }>(
        '/api/square/damages/workspaces', { method: 'POST', body: payload },
      ),
    entries: (workspace_id: string) =>
      apiRequest<{ ok: true; entries: Array<Record<string, unknown>>; readiness: { score_percent: number; basis: Record<string, unknown> } }>(
        '/api/square/damages/entries', { method: 'GET', query: { workspace_id } },
      ),
    entryAction: (payload: Record<string, unknown>) =>
      apiRequest<{ ok: boolean; entry?: Record<string, unknown>; entry_id?: string; status?: string; message?: string }>(
        '/api/square/damages/entries', { method: 'POST', body: payload },
      ),
  },

  // Square Code provider-integrations lane (/square/providers) — Phase 1,
  // READ-ONLY. Every call is server-mediated: the browser NEVER talks to
  // Daily / ElevenLabs / Vercel / Supabase directly, and no secret value is
  // ever returned. Unconfigured providers report `provider_not_configured`.
  providers: {
    // Aggregate configured / not-configured status for all four providers.
    // Presence-only — no vendor calls, no secret values.
    status: () =>
      apiRequest<{
        ok: true
        lane: 'square-provider-integrations'
        phase: number
        providers: Array<{
          provider: 'daily' | 'elevenlabs' | 'vercel' | 'supabase'
          label: string
          configured: boolean
          status: 'configured' | 'provider_not_configured' | 'provider_error'
          required_env_present: Record<string, boolean>
          optional_env_present: Record<string, boolean>
          missing_required_env_names: string[]
          last_checked_at: string
        }>
        checked_at: string
      }>('/api/square/providers/status', { method: 'GET' }),

    // Read-only Vercel visibility (deployments / domains / env-presence).
    vercelStatus: () =>
      apiRequest<
        | {
            ok: true
            configured: true
            provider: 'vercel'
            visibility: {
              project_ref: string | null
              latest_production: VercelDeploymentRow | null
              latest_preview: VercelDeploymentRow | null
              domains: VercelDomainRow[]
              env_presence: { project_id: boolean; scope: boolean; org_id: boolean }
            }
            checked_at: string
          }
        | { ok: false; configured: false; provider: 'vercel'; reason: 'provider_not_configured'; missing: string[]; checked_at: string }
        | { ok: false; configured: true; provider: 'vercel'; reason: 'provider_error'; error: string; checked_at: string }
      >('/api/square/providers/vercel/status', { method: 'GET' }),

    // Read-only Supabase Management visibility (project / functions / secret
    // PRESENCE by name — never a secret value).
    supabaseStatus: () =>
      apiRequest<
        | {
            ok: true
            configured: true
            provider: 'supabase'
            visibility: {
              project: { ref: string; name: string | null; region: string | null; status: string | null; created_at: string | null } | null
              functions: Array<{ slug: string; name: string | null; status: string | null; version: number | null; updated_at: string | null }>
              secrets: { count: number; names: string[] }
            }
            checked_at: string
          }
        | { ok: false; configured: false; provider: 'supabase'; reason: 'provider_not_configured'; missing: string[]; checked_at: string }
        | { ok: false; configured: true; provider: 'supabase'; reason: 'provider_error'; error: string; checked_at: string }
      >('/api/square/providers/supabase/status', { method: 'GET' }),
  },

  workspace: {
    list: (q?: { scope?: 'personal' | 'project'; project_id?: string; current_mode?: string; pinned?: boolean; limit?: number }) =>
      apiRequest<{ ok: true; workspaces: WorkspaceApiRow[] }>('/api/workspace/list', {
        method: 'GET',
        query: {
          scope: q?.scope,
          project_id: q?.project_id,
          current_mode: q?.current_mode,
          pinned: q?.pinned == null ? undefined : q.pinned ? 1 : 0,
          limit: q?.limit,
        },
      }),
    get: (id: string) =>
      apiRequest<{ ok: true; workspace: any; sections: any[]; latest_run: any | null }>(
        '/api/workspace/get', { method: 'GET', query: { id } },
      ),
    runs: (q: { project_id?: string; workspace_id?: string; limit?: number }) =>
      apiRequest<{
        ok: true
        workspace_ids: string[]
        grouped_counts: Record<string, number>
        latest_run: WorkspaceRunApiRow | null
        runs: WorkspaceRunApiRow[]
      }>('/api/workspace/runs', { method: 'GET', query: q }),
    events: (q: { project_id?: string; workspace_id?: string; limit?: number }) =>
      apiRequest<{
        ok: true
        workspace_ids: string[]
        events: WorkspaceEventApiRow[]
      }>('/api/workspace/events', { method: 'GET', query: q }),
    create: (payload: { name: string; scope: 'personal' | 'project'; projectId?: string; mode?: string }) =>
      apiRequest<{ workspace: WorkspaceApiRow }>('/api/workspace/create', { method: 'POST', body: payload }),
    run: (payload: { workspaceId: string; mode: string; prompt: string; provider?: string; inputJson?: Record<string, unknown> }) =>
      apiRequest<{ run: any; dispatch: { ok: boolean; inline: boolean; provider_job_id?: string; error?: string } }>(
        '/api/workspace/run', { method: 'POST', body: payload },
      ),
  },

  site: {
    get: (projectId: string) =>
      apiRequest<{ site: SiteApiRow | null }>('/api/site/sites', {
        method: 'GET',
        query: { projectId },
      }),
    create: (payload: { projectId: string; projectName: string }) =>
      apiRequest<{ site: SiteApiRow; created: boolean }>('/api/site/sites', {
        method: 'POST',
        body: payload,
      }),
    pages: (siteId: string) =>
      apiRequest<{ pages: SitePageApiRow[] }>('/api/site/pages', {
        method: 'GET',
        query: { siteId },
      }),
    blocks: (pageId: string) =>
      apiRequest<{ blocks: Array<SiteBlockApiRow & { assets?: Array<{ id: string }> }> }>('/api/site/blocks', {
        method: 'GET',
        query: { pageId },
      }),
    // Block CRUD — backed by api/site/blocks.ts (PATCH/POST/DELETE).
    // Audited via OliviumAudit on every write. Owner-checked at the
    // route level via assertOwnsBlock / assertOwnsPage.
    createBlock: (payload: {
      pageId: string
      blockType: string
      contentJson?: Record<string, unknown>
      styleJson?: Record<string, unknown>
      insertAt?: number
    }) =>
      apiRequest<{ block: SiteBlockApiRow }>('/api/site/blocks', {
        method: 'POST',
        body: payload,
      }),
    updateBlock: (
      blockId: string,
      updates: {
        content_json?: Record<string, unknown>
        style_json?: Record<string, unknown>
        visibility?: 'visible' | 'hidden' | 'conditional'
        locked?: boolean
        metadata_json?: Record<string, unknown>
      },
    ) =>
      apiRequest<{ ok: true }>('/api/site/blocks', {
        method: 'PATCH',
        body: { blockId, updates },
      }),
    deleteBlock: (blockId: string) =>
      apiRequest<{ ok: true }>('/api/site/blocks', {
        method: 'DELETE',
        query: { blockId },
      }),
    reorderBlock: (payload: { pageId: string; blockId: string; newOrder: number }) =>
      apiRequest<{ ok: true }>('/api/site/blocks', {
        method: 'POST',
        body: { action: 'reorder', ...payload },
      }),
    deployments: (siteId: string, limit?: number) =>
      apiRequest<{ deployments: SiteDeploymentHistoryApiRow[] }>('/api/site/deployments', {
        method: 'GET',
        query: { siteId, limit },
      }),
    // Publish — creates a real sq_site_deployments row + dispatches
    // through api/_lib/providers/site-deploy.ts. provider defaults
    // to 'manual' so live mode never invents a synthetic deploy URL
    // when the operator hasn't chosen a real backend yet.
    publish: (payload: { siteId: string; provider?: 'mock' | 'vercel' | 'netlify' | 'manual' | 'export' | 'internal' }) =>
      apiRequest<{
        deployment: SiteDeploymentHistoryApiRow
        dispatch: { ok: boolean; status: string; provider_ref?: string | null; deploy_url?: string | null; error?: string }
      }>('/api/site/deployments', {
        method: 'POST',
        body: payload,
      }),
    assets: (siteId: string, limit?: number) =>
      apiRequest<{ assets: SiteAssetApiRow[] }>('/api/site/assets', {
        method: 'GET',
        query: { siteId, limit },
      }),
    revisions: (siteId: string, limit?: number) =>
      apiRequest<{ revisions: SiteRevisionApiRow[] }>('/api/site/revisions', {
        method: 'GET',
        query: { siteId, limit },
      }),
  },

  hosting: {
    deployments: (q: { project_id: string; environment?: string; limit?: number }) =>
      apiRequest<{
        ok: true
        deployments: HostingDeploymentApiRow[]
        latest_by_environment: Record<string, HostingDeploymentApiRow | null>
      }>('/api/hosting/deployments/list', {
        method: 'GET',
        query: q,
      }),
    triggerDeployment: (payload: {
      project_id: string
      site_id?: string | null
      environment: 'production' | 'preview' | 'local'
      provider?: string
    }) =>
      apiRequest<{ ok: true; deployment: HostingDeploymentApiRow }>(
        '/api/hosting/deployments/trigger',
        { method: 'POST', body: payload },
      ),
    domains: (project_id: string) =>
      apiRequest<{ ok: true; domains: HostingDomainApiRow[] }>(
        '/api/hosting/domains/list',
        { method: 'GET', query: { project_id } },
      ),
    addDomain: (payload: {
      project_id: string
      hostname: string
      environment: 'production' | 'preview' | 'local'
    }) =>
      apiRequest<{ ok: true; domain: HostingDomainApiRow }>(
        '/api/hosting/domains/add',
        { method: 'POST', body: payload },
      ),
    verifyDomain: (domain_id: string) =>
      apiRequest<{
        ok: true
        domain: HostingDomainApiRow
        verification: { status: string; message: string }
      }>('/api/hosting/domains/verify', {
        method: 'POST',
        body: { domain_id },
      }),
    deleteDomain: (domain_id: string) =>
      apiRequest<{ ok: true; deleted: string }>(
        '/api/hosting/domains/delete',
        { method: 'POST', body: { domain_id } },
      ),
    /**
     * Live Vercel project status snapshot. Returns one of three states:
     *   ok         — real provider data (deployments + domains)
     *   unavailable — provider token not configured (honest "not wired" state)
     *   provider_error — Vercel returned an error (honest failure)
     */
    vercelProjectStatus: (project_name: string) =>
      apiRequest<
        | {
            ok: true
            unavailable: false
            project_name: string
            latest_production: VercelDeploymentRow | null
            latest_preview: VercelDeploymentRow | null
            domains: VercelDomainRow[]
            checked_at: string
          }
        | {
            ok: false
            unavailable: true
            reason: 'provider_token_missing'
            checked_at: string
          }
        | {
            ok: false
            unavailable: false
            reason: 'provider_error'
            error: string
            checked_at: string
          }
      >('/api/hosting/vercel/project-status', {
        method: 'GET',
        query: { project_name },
      }),
  },

  commands: {
    dispatch: (payload: Record<string, unknown>) =>
      apiRequest<{ result: CommandApiResult }>('/api/commands/dispatch', {
        method: 'POST',
        body: payload,
      }),
    // Compass NL interpreter — turns a free-text utterance into a
    // resolved registry intent. Server-only Claude call; the key never
    // reaches the browser. 503 `interpret_failed` (skipped) when
    // ANTHROPIC_API_KEY is unset — the caller surfaces that honestly.
    // This route interprets only; execution still goes through dispatch.
    interpret: (payload: {
      input_text: string
      surface?: string
      surface_id?: string | null
      input_locale?: string | null
    }) =>
      apiRequest<{ intent: CommandIntent; model: string | null }>(
        '/api/commands/interpret',
        { method: 'POST', body: payload },
      ),
  },

  voice: {
    transcribeInline: (payload: {
      audioBase64: string
      contentType?: string
      filename?: string
      language?: string
    }) =>
      apiRequest<{
        transcript: {
          text: string
          segments: Array<{ start: number; end: number; text: string; speaker: number | null }>
          language: string | null
          job_id: string | null
        }
      }>('/api/voice/transcribe', { method: 'POST', body: payload }),
    // ElevenLabs TTS — the frontend sends TEXT and receives base64 audio. The
    // ElevenLabs api key stays server-side and is NEVER in this response. Play
    // via new Audio('data:audio/mpeg;base64,'+audio_base64). (See speakStatus.)
    tts: (payload: { text: string; voiceId?: string; modelId?: string; voiceSettings?: Record<string, number | boolean> }) =>
      apiRequest<{
        ok: true
        audio_base64: string
        content_type: string
        voice_id: string | null
        model_id: string | null
        chars: number
      }>('/api/voice/tts', { method: 'POST', body: payload }),
    // Short-lived signed URL for the ElevenLabs browser conversational agent —
    // NOT the api key. 503 `voice_not_configured` until the agent env is set.
    token: (payload: { agentId?: string } = {}) =>
      apiRequest<{ ok: true; signed_url: string; agent_id: string | null }>(
        '/api/voice/token', { method: 'POST', body: payload },
      ),
  },

  orgs: {
    list: () =>
      apiRequest<{ ok: true; orgs: Array<{ id: string; name: string; slug: string; vertical: string; owner_id: string; created_at: string }> }>(
        '/api/orgs/list', { method: 'GET' },
      ),
    create: (payload: { name: string; slug?: string; vertical?: string }) =>
      apiRequest<{ ok: true; org: any }>('/api/orgs/create', { method: 'POST', body: payload }),
  },

  olivium: {
    audit: (event: {
      action: string
      resource_type: string
      resource_id?: string | null
      detail?: Record<string, unknown>
      severity?: 'info' | 'warning' | 'critical' | 'alert'
      outcome?: 'success' | 'denied' | 'error' | 'blocked'
    }) => apiRequest('/api/olivium/audit', { method: 'POST', body: event }),
    seal: (document_id: string, document_hash: string) =>
      apiRequest('/api/olivium/seal', { method: 'POST', body: { document_id, document_hash } }),
    verifyChain: (limit = 200) =>
      apiRequest('/api/olivium/verify-chain', { method: 'GET', query: { limit } }),
    alerts: (q?: {
      status?: string
      severity?: string
      limit?: number
    }) =>
      apiRequest<{ ok: true; alerts: OliviumAlertApiRow[] }>(
        '/api/olivium/alerts',
        { method: 'GET', query: q },
      ),
    updateAlert: (payload: {
      id: string
      status: 'acknowledged' | 'investigating' | 'resolved' | 'dismissed' | 'escalated'
    }) =>
      apiRequest<{ ok: true; alert: OliviumAlertApiRow }>(
        '/api/olivium/alerts/update',
        { method: 'POST', body: payload },
      ),
  },

  support: {
    list: (q?: { scope?: 'mine' | 'assigned' | 'all'; status?: string; priority?: string; vertical?: string; q?: string; limit?: number; offset?: number }) =>
      apiRequest<{ ok: true; rows: any[]; total: number | null; scope: string; role: string | null }>('/api/support/list', { method: 'GET', query: q }),
    get: (id: string) =>
      apiRequest<{ ok: true; request: any; messages: any[] }>('/api/support/get', { method: 'GET', query: { id } }),
    create: (payload: { subject: string; body?: string; priority?: string; vertical?: string }) =>
      apiRequest<{ ok: true; request: any }>('/api/support/create', { method: 'POST', body: payload }),
    update: (payload: { id: string; status?: string; priority?: string; assigned_to?: string | null; message?: string }) =>
      apiRequest<{ ok: true; request: any }>('/api/support/update', { method: 'POST', body: payload }),
    assign: (payload: { id: string; assigned_to: string | null }) =>
      apiRequest<{ ok: true; request: any }>('/api/support/assign', { method: 'POST', body: payload }),
  },

  comms: {
    channels: () =>
      apiRequest<{ ok: true; channels: Array<{ id: string; name: string; channel_type: string; description: string | null; sort_order: number; active: boolean; workspace_id: string | null }> }>(
        '/api/comms/channels', { method: 'GET' },
      ),
    messages: (channel: string, limit = 50) =>
      apiRequest<{ ok: true; channel: string; messages: Array<{ id: string; channel_id: string; sender_type: string; sender_id: string; body: string; message_type: string; severity: string | null; created_at: string }> }>(
        '/api/comms/messages', { method: 'GET', query: { channel, limit } },
      ),
    post: (payload: { channel_id: string; body: string; message_type?: string; severity?: string }) =>
      apiRequest<{ ok: true; message: any }>('/api/comms/post', { method: 'POST', body: payload }),
    events: (q?: {
      source?: string
      severity?: string
      project_id?: string
      workspace_id?: string
      deployment_id?: string
      command_id?: string
      delivery?: string
      limit?: number
    }) =>
      apiRequest<{
        ok: true
        events: Array<{
          id: string
          owner_user_id: string
          project_id: string | null
          workspace_id: string | null
          deployment_id: string | null
          command_id: string | null
          source_system: string
          event_type: string
          severity: 'info' | 'warning' | 'critical' | 'success' | 'error'
          title: string
          body: string | null
          payload_json: Record<string, unknown>
          external_target: string | null
          external_ref: string | null
          delivery_status:
            | 'pending'
            | 'sent'
            | 'failed'
            | 'skipped'
            | 'not_applicable'
          created_at: string
          updated_at: string
        }>
        checked_at: string
      }>('/api/comms/events', { method: 'GET', query: q as any }),
    health: () =>
      apiRequest<{
        ok: true
        slack: {
          configured: boolean
          webhook: { resolved: boolean; via: string | null; aliases: readonly string[] }
        }
        email: {
          configured: boolean
          api_key: { resolved: boolean; via: string | null; aliases: readonly string[] }
          from_address: { resolved: boolean; via: string | null; aliases: readonly string[] }
        }
        comms_table: { ready: boolean; error: string | null }
        last_slack_delivery_at: string | null
        retries_available: boolean
        checked_at: string
      }>('/api/comms/health', { method: 'GET' }),
    retry: (id: string) =>
      apiRequest<{ ok: boolean; status: string; noop?: boolean; note?: string }>(
        '/api/comms/retry', { method: 'POST', body: { id } },
      ),
    test: (target: 'slack' | 'email', message?: string) =>
      apiRequest<{
        ok: boolean
        target: string
        event_id: string | null
        delivery_status: string | null
        note?: string
      }>('/api/comms/test', { method: 'POST', body: { target, message } }),
  },

  files: {
    list: (bucket = 'files', project_id?: string) =>
      apiRequest<{ ok: true; bucket: string | null; project_id: string | null; files: Array<{ id: string; name: string; project_id: string | null; bucket: string; path: string; mime: string | null; size: number | null; status: string; released_at: string | null; created_at: string | null; updated_at: string | null }> }>(
        '/api/files/list', { method: 'GET', query: { bucket, project_id } },
      ),
    uploadUrl: (bucket: string, path: string, mime_type?: string, opts?: { project_id?: string; size_bytes?: number }) =>
      apiRequest<{ ok: true; document_id: string | null; bucket: string; path: string; project_id: string | null; token: string; signedUrl: string }>(
        '/api/files/upload-url', { method: 'POST', body: { bucket, path, mime_type, project_id: opts?.project_id, size_bytes: opts?.size_bytes } },
      ),
    download: (bucket: string, path: string, opts?: { document_id?: string }) =>
      apiRequest<{ ok: true; signedUrl: string }>('/api/files/download', {
        method: 'GET', query: { bucket, path, document_id: opts?.document_id },
      }),
    delete: (bucket: string, path: string, opts?: { document_id?: string }) =>
      apiRequest<{ ok: true; record_removed: boolean }>('/api/files/delete', {
        method: 'POST', body: { bucket, path, document_id: opts?.document_id },
      }),
  },

  // Server-truth capability manifest (packet §05). Bare manifest, no
  // { ok } envelope. Consumed through useCapabilities().
  capabilities: {
    get: () =>
      apiRequest<CapabilityManifest>('/api/capabilities', { method: 'GET' }),
  },

  admin: {
    rotateKeys: (reason: string) =>
      apiRequest<{ ok: true; rotated: true; newPublicKeyPrefix: string }>(
        '/api/admin/rotate-keys', { method: 'POST', body: { reason } },
      ),
    revokeSession: (session_id: string, reason: string) =>
      apiRequest<{ ok: true }>('/api/admin/revoke-session', { method: 'POST', body: { session_id, reason } }),
    securityPosture: () =>
      apiRequest<{
        ok: true
        posture: {
          active_sessions: number
          active_lockouts: number
          open_alerts: number
          critical_alerts: number
          active_api_keys: number
          denials_today: number
          auth_hardening: { rate_limiting: boolean; lockout_enabled: boolean; olivium_audit: boolean; device_trust: boolean }
          rls_coverage: string
        }
        checked_at: string
      }>('/api/admin/security-posture', { method: 'GET' }),
    databaseStatus: () =>
      apiRequest<{
        ok: true
        database: {
          migration_files: number
          critical_tables_checked: number
          critical_tables_present: number
          missing_tables: string[]
          by_prefix: { olv: number; sq: number; sc: number }
          rls_enforced: boolean
          table_detail: Record<string, boolean>
        }
        checked_at: string
      }>('/api/admin/database-status', { method: 'GET' }),
    storageStatus: () =>
      apiRequest<{
        ok: true
        storage: {
          required_buckets: number
          present_buckets: number
          all_present: boolean
          buckets: Array<{ name: string; purpose: string; visibility: string; exists: boolean; error: string | null }>
        }
        checked_at: string
      }>('/api/admin/storage-status', { method: 'GET' }),
    keys: {
      list: (q?: { status?: string; limit?: number }) =>
        apiRequest<{
          ok: true
          keys: Array<{
            id: string; label: string; prefix: string; tier: string; status: string
            created_at: string; revoked_at: string | null; last_used_at: string | null; created_by: string | null
          }>
          total: number
        }>('/api/admin/keys/list', { method: 'GET', query: q }),
      issue: (payload: { label: string; tier?: string }) =>
        apiRequest<{
          ok: true; key_id: string; prefix: string; plaintext: string; label: string; tier: string
        }>('/api/admin/keys/issue', { method: 'POST', body: payload }),
      revoke: (key_id: string, reason: string) =>
        apiRequest<{ ok: true; key_id: string; revoked: true }>(
          '/api/admin/keys/revoke', { method: 'POST', body: { key_id, reason } },
        ),
    },
    slackMappings: {
      list: () =>
        apiRequest<{
          ok: true
          mappings: Array<{
            id: string; slack_user_id: string; slack_team_id: string | null
            supabase_user_id: string; slack_username: string | null
            slack_display_name: string | null; verified: boolean
            created_at: string; updated_at: string
          }>
          total: number
        }>('/api/admin/slack-mappings/list', { method: 'GET' }),
      upsert: (payload: {
        slack_user_id: string; slack_team_id?: string; supabase_user_id: string
        slack_username?: string; slack_display_name?: string; verified?: boolean
      }) =>
        apiRequest<{ ok: true; mapping: Record<string, unknown> }>(
          '/api/admin/slack-mappings/upsert', { method: 'POST', body: payload },
        ),
      delete: (mapping_id: string) =>
        apiRequest<{ ok: true; deleted: string }>(
          '/api/admin/slack-mappings/delete', { method: 'POST', body: { mapping_id } },
        ),
    },
  },

  workflows: {
    trigger: (payload: { workflow_slug: string; trigger_payload?: unknown; project_id?: string }) =>
      apiRequest<{
        ok: boolean
        execution_id: string | null
        status: string
        steps_completed: number
        steps_failed: number
        steps_skipped: number
        error: string | null
      }>('/api/workflows/trigger', { method: 'POST', body: payload }),
    listExecutions: (q?: { limit?: number }) =>
      apiRequest<{
        ok: true
        executions: Array<{
          id: string
          workflow_id: string
          project_id: string | null
          owner_user_id: string
          triggered_by: string
          status: string
          started_at: string | null
          completed_at: string | null
          error_message: string | null
          compiled_plan: { workflow_slug?: string; steps?: Array<{ id: string; action: string; label: string }> }
          created_at: string
          updated_at: string
          steps: Array<{
            id: string
            execution_id: string
            step_id: string
            step_action: string
            step_order: number
            status: string
            input: unknown
            output: unknown
            error_message: string | null
            started_at: string | null
            completed_at: string | null
            attempt: number
            max_attempts: number
            created_at: string
          }>
        }>
        total: number
      }>('/api/workflows/list', { method: 'GET', query: { type: 'executions', limit: q?.limit } }),
    getExecution: (executionId: string) =>
      apiRequest<{
        ok: true
        execution: any
        steps: any[]
      }>('/api/workflows/list', { method: 'GET', query: { execution_id: executionId } }),
  },

  health: {
    all: () =>
      apiRequest<{
        status: 'ok' | 'degraded' | 'down' | 'skipped'
        durationMs: number
        checkedAt: string
        checks: Record<string, { name: string; status: string; durationMs: number; detail: string; meta?: unknown }>
        // Non-secret build identity of the deployment that answered (Gate 8).
        release: { environment: string | null; commit: string | null; region: string | null; deployment_host: string | null }
      }>('/api/health/all', { method: 'GET' }),
    redis:  () => apiRequest('/api/health/redis',  { method: 'GET' }),
    providers: () =>
      apiRequest<{
        providers: Record<
          string,
          { resolved: boolean; via: string | null; aliases: readonly string[] }
        >
        checked_at: string
      }>('/api/health/providers', { method: 'GET' }),
    slack: () =>
      apiRequest<{
        ok: true
        slack: {
          configured: boolean
          webhook: { resolved: boolean; via: string | null; aliases: readonly string[] }
          bot_token: { resolved: boolean; via: string | null; aliases: readonly string[] }
          signing_secret: { resolved: boolean; via: string | null; aliases: readonly string[] }
        }
        checked_at: string
      }>('/api/health/slack', { method: 'GET' }),
    sentry: () =>
      apiRequest<{
        ok: true
        sentry: {
          ingestion: {
            server_envelope: { configured: boolean; via: string | null }
            browser_dsn: { configured: boolean; via: string | null }
          }
          release_management: {
            auth_token: { configured: boolean; via: string | null }
            org: { configured: boolean; via: string | null }
            project: { configured: boolean; via: string | null }
          }
          drains: {
            otlp_traces: { configured: boolean; via: string | null }
            vercel_log_drain: { configured: boolean; via: string | null }
          }
          release: { sha: string | null; via: string | null }
          environment: string | null
          ready: boolean
        }
        checked_at: string
      }>('/api/health/sentry', { method: 'GET' }),
    voiceLoop: () =>
      apiRequest<{
        ok: true
        checked_at: string
        voice_loop: {
          commands_dispatch_route: string
          voice_transcribe_route: string
          commands_table_ready: boolean
          commands_table_error: string | null
          gladia: { configured: boolean; via: string | null; aliases: readonly string[] }
          slack: { configured: boolean; via: string | null; aliases: readonly string[] }
          browser_speech_synthesis: 'detect_in_browser'
        }
      }>('/api/health/voice-loop', { method: 'GET' }),
  },

  tricorder: {
    clients: () =>
      apiRequest<{ ok: true; clients: TricorderClient[] }>(
        '/api/tricorder/clients', { method: 'GET' },
      ),
    updates: {
      list: (project_id: string) =>
        apiRequest<{ ok: true; updates: TricorderUpdate[] }>(
          '/api/tricorder/updates', { method: 'GET', query: { project_id } },
        ),
      create: (payload: { project_id: string; update_type?: string; title: string; body?: string }) =>
        apiRequest<{ ok: true; update: TricorderUpdate }>(
          '/api/tricorder/updates', { method: 'POST', body: payload },
        ),
    },
    threads: {
      list: (project_id: string) =>
        apiRequest<{ ok: true; threads: TricorderThread[] }>(
          '/api/tricorder/threads', { method: 'GET', query: { project_id } },
        ),
      messages: (thread_id: string) =>
        apiRequest<{ ok: true; messages: TricorderMessage[] }>(
          '/api/tricorder/threads', { method: 'GET', query: { thread_id } },
        ),
      create: (payload: { project_id: string; subject: string }) =>
        apiRequest<{ ok: true; thread: TricorderThread }>(
          '/api/tricorder/threads', { method: 'POST', body: payload },
        ),
      reply: (payload: { thread_id: string; body: string }) =>
        apiRequest<{ ok: true; message: TricorderMessage }>(
          '/api/tricorder/threads', { method: 'POST', body: { action: 'message', ...payload } },
        ),
      updateStatus: (payload: { thread_id: string; status: string }) =>
        apiRequest<{ ok: true; thread: TricorderThread }>(
          '/api/tricorder/threads', { method: 'PATCH', body: payload },
        ),
    },
    health: {
      get: (project_id: string) =>
        apiRequest<{ ok: true; health: TricorderHealth | null }>(
          '/api/tricorder/health', { method: 'GET', query: { project_id } },
        ),
      upsert: (payload: {
        project_id: string; status?: string; health_score?: number | null
        gitlab_repo?: string | null; notes?: string | null
      }) =>
        apiRequest<{ ok: true; health: TricorderHealth }>(
          '/api/tricorder/health', { method: 'POST', body: payload },
        ),
    },
  },
}
